import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { calculateSalesSummary, formatCurrency, isOfflineMethod, describePaymentChannel } from "./salesDashboardUtils";
import { fetchMonthlyCashTotals, fetchCashControlData } from "../../../../core/api/report";

dayjs.extend(isoWeek);

export const SALES_DUMP_EXPORT_TEMPLATE = {
  sheetName: "nal_salesdump",
  // Header labels + column order mirror the PAS RADE "Sales Dump" export the
  // accountant reconciles against (readable names, plus "Source File" and
  // "Original Order Num"). Each tuple is [headerLabel, rowFieldKey, formatType?].
  columns: [
    ["Source File", "source_file"],
    ["Company", "company"],
    ["Purch Date", "purch_date", "datetimeText"],
    ["Order Num", "order_num"],
    ["Original Order Num", "original_order_num"],
    ["Customer ID", "customer_id"],
    ["Name", "name"],
    ["Address1", "address1"],
    ["Address2", "address2"],
    ["City", "city"],
    ["State", "state"],
    ["ZIP", "zip"],
    ["Mail Code", "mail_code"],
    ["Mail Date", "mail_date", "datetimeText"],
    ["Campaign Offer Code", "offer_code"],
    ["Campaign Prod Code", "camp_prod_code"],
    ["Campaign Prod Desc", "camp_prod_desc"],
    ["Campaign Category", "category"],
    ["Source", "source"],
    ["List", "list"],
    ["Segment", "segment"],
    ["Order Date \u2013 Mail Date", "order_date_mail_date"],
    ["Today \u2013 Order Date", "today_order_date"],
    ["Order Sale Amt", "order_sale_amt"],
    ["Product Sales Amt", "product_sales_amt"],
    ["Xsell Sales Amt", "xsell_sales_amt"],
    ["Shipping", "shipping"],
    ["Refund Amount", "refund_amount"],
    ["Total Cost", "total_cost"],
    ["Product Cost", "product_cost"],
    ["Ship Cost", "ship_cost"],
    ["Merch Fee", "merch_fee"],
    ["Fulfill Cost", "fulfill_cost"],
    ["Callcenter Cost", "callcenter_cost"],
    ["Royalty Amt", "royalty_amt"],
    ["Refund Cost", "refund_cost"],
    ["Phone Sales", "phone_sales"],
    ["Mail Sales", "mail_sales"],
    ["Web Sales", "web_sales"],
    ["Unknown Sales", "unknown_sales"],
    ["Prod Code 1", "prodcode1"],
    ["Prod Code 2", "prodcode2"],
    ["Prod Code 3", "prodcode3"],
    ["Prod Code 4", "prodcode4"],
    ["Prod Code 5", "prodcode5"],
    ["Prod Code 6", "prodcode6"],
    ["Prod Code 7", "prodcode7"],
    ["Prod Code 8", "prodcode8"],
    ["Prod Code 9", "prodcode9"],
    ["Prod Qty 1", "prodqty1"],
    ["Prod Qty 2", "prodqty2"],
    ["Prod Qty 3", "prodqty3"],
    ["Prod Qty 4", "prodqty4"],
    ["Prod Qty 5", "prodqty5"],
    ["Prod Qty 6", "prodqty6"],
    ["Prod Qty 7", "prodqty7"],
    ["Prod Qty 8", "prodqty8"],
    ["Prod Qty 9", "prodqty9"],
    ["Prod Sales 1", "prodsale1"],
    ["Prod Sales 2", "prodsale2"],
    ["Prod Sales 3", "prodsale3"],
    ["Prod Sales 4", "prodsale4"],
    ["Prod Sales 5", "prodsale5"],
    ["Prod Sales 6", "prodsale6"],
    ["Prod Sales 7", "prodsale7"],
    ["Prod Sales 8", "prodsale8"],
    ["Prod Sales 9", "prodsale9"],
  ],
};

export const MAILING_LTV_EXPORT_TEMPLATE = {
  sheetName: "mlgresponse_ltv_combined",
  columns: [
    ["Company", "company"],
    ["Group Code", "group_code"],
    ["Mailing Code", "mailing_code"],
    ["Mlg Desc", "mailing_description"],
    ["Mail Date", "mail_date", "excelDate"],
    ["Offer", "offer"],
    ["Offer Desc", "offer_description"],
    ["Product", "product"],
    ["Product Desc", "product_description"],
    ["Category", "category"],
    ["Source Code", "source_code"],
    ["List", "mailing_list"],
    ["Segment", "segment"],
    ["Ship Qty", "shipped_quantity"],
    ["Mailed", "mailed_quantity"],
    ["Net %", "net_percentage"],
    ["Printing", "printing_cost"],
    ["Lists", "list_cost"],
    ["Postage", "postage_cost"],
    ["Lettershop", "lettershop_cost"],
    ["DP", "dp_cost"],
    ["Misc", "misc_cost"],
    ["Tot Mlg Cost", "total_mailing_cost"],
    ["Mail Orders", "mail_orders"],
    ["Phone Orders", "phone_orders"],
    ["Web Orders", "web_orders"],
    ["Unk Orders", "unknown_orders"],
    ["Gross Orders", "gross_orders"],
    ["Gross %", "gross_percentage"],
    ["Net Orders", "net_orders"],
    ["Net %", "net_percentage_orders"],
    ["Loss Rate %", "loss_rate_percentage"],
    ["Xsell Orders", "cross_sell_orders"],
    ["Refund Orders", "refund_orders"],
    ["Subs", "subscriptions"],
    ["Active Subs", "active_subscriptions"],
    ["Inquirers", "inquiries"],
    ["Backorders", "backorders"],
    ["B/O Amt", "backorder_amount"],
    ["% with B/O", "percentage_with_backorders"],
    ["Prod Amt", "product_amount"],
    ["X-Sell Amt", "cross_sell_amount"],
    ["Misc Amt", "misc_amount"],
    ["Non CC Amt", "non_credit_card_amount"],
    ["CC Amt", "credit_card_amount"],
    ["Auto-Ships", "auto_ships"],
    ["Gross Sales", "gross_sales"],
    ["Phone Sales", "phone_sales"],
    ["Mail Sales", "mail_sales"],
    ["Web Sales", "web_sales"],
    ["Unknown Sales", "unknown_sales"],
    ["Turnover/Mail (RPP)", "turnover_per_mail"],
    ["Avg Sale Value", "average_sale_value"],
    ["Refunds", "refunds"],
    ["Refund Rate %", "refund_rate_percentage"],
    ["Net Sales", "net_sales"],
    ["Product Cost", "product_cost"],
    ["Ship Cost", "shipping_cost"],
    ["Fulfill Cost", "fulfillment_cost"],
    ["Refund Cost", "refund_cost"],
    ["Call Ctr", "call_center_cost"],
    ["Merch Fee", "merchant_fee"],
    ["Royalties", "royalties"],
    ["Total Cost", "total_cost"],
    ["Cost/Mail (CPP)", "cost_per_mail"],
    ["Net P/L", "net_profit_loss"],
    ["Profit %", "profit_percentage"],
    ["Net ROI", "net_roi"],
    ["% Breakeven", "percentage_breakeven"],
    ["Breakeven % Resp", "breakeven_percentage_response"],
    ["", "unnamed_80"],
    ["Net Per Piece", "net_per_piece"],
    ["Avg Order", "average_order_value"],
    ["Avg w/Autoship", "average_with_autoship"],
    ["Avg Turns", "average_turns"],
    ["Mlg Cost", "mailing_cost"],
    ["Product Cost", "product_cost1"],
    ["Call Ctr", "call_center_cost1"],
    ["Merch Fee", "merchant_fee1"],
    ["Total Cost", "total_cost1"],
    ["Net PL/Order", "net_profit_loss_per_order"],
    ["Avg w/Autoship", "average_with_autoship1"],
    ["Mlg Cost", "mailing_cost1"],
    ["Product Cost", "product_cost2"],
    ["Call Ctr", "call_center_cost2"],
    ["Merch Fee", "merchant_fee2"],
    ["Total Cost", "total_cost2"],
    ["Net PL/M", "net_profit_loss_per_mail"],
    ["NSF Count", "nsf_count"],
    ["Days ", "days_count"],
    ["FE AOV", "first_event_aov"],
    ["BE AOV", "breakeven_aov"],
    ["LT AOV", "long_term_aov"],
    ["Qty Mailed", "quantity_mailed"],
    ["NTF Buyers", "ntf_buyers"],
    ["FE ROR % (LTV)", "fe_ror_ltv"],
    ["FE Cost", "fe_cost"],
    ["FE CPO", "fe_cpo"],
    ["FE Purch", "fe_purchases"],
    ["FE P/L (LTV)", "fe_profit_loss_ltv"],
    ["FE AOV", "fe_aov1"],
    ["FE ROI", "fe_roi"],
    ["Subs %", "subscriptions_percentage"],
    ["BE Orders", "be_orders"],
    ["BE Mlg Qty", "be_mailed_quantity"],
    ["BE ROR % (LTV)", "be_ror_ltv"],
    ["BE Cost", "be_cost"],
    ["BE CPO", "be_cpo"],
    ["BE Purch", "be_purchases"],
    ["BE P/L (LTV)", "be_profit_loss_ltv"],
    ["BE PL/Buyer (LTV)", "be_profit_loss_per_buyer_ltv"],
    ["Reorder Rate (LTV)", "reorder_rate_ltv"],
    ["BE AOV", "be_aov1"],
    ["Tot Purch", "total_purchases"],
    ["Tot Cost", "tot_cost"],
    ["Net P/L", "net_profit_loss_ltv"],
    ["LT AOV", "lt_aov1"],
    ["LT ROI", "lt_roi"],
    ["LTV FE P/L per Buyer", "fe_p_l_ltv1"],
    ["LTV BE P/L per Buyer", "be_pl_buyer_ltv2"],
    ["LTV Total P/L per Buyer ", "pl_per_buyer_total"],
    ["Action", "action"],
  ],
};


/**
 * Extract rows currently visible in the AG Grid after all filters and sorting.
 * Falls back to the provided rawData if the grid API isn't available.
 */
export const getFilteredRowsFromGrid = (gridApi, rawData) => {
  if (!gridApi || typeof gridApi.forEachNodeAfterFilterAndSort !== "function") {
    return rawData || [];
  }
  const rows = [];
  gridApi.forEachNodeAfterFilterAndSort((node) => {
    if (node.data) rows.push(node.data);
  });
  return rows.length > 0 ? rows : rawData || [];
};

/**
 * Resolve visible columns in the order the user sees them.
 * Uses runtime grid state when available, falls back to static column defs.
 */
const resolveVisibleColumns = (gridApi, staticCols) => {
  if (gridApi && typeof gridApi.getAllDisplayedColumns === "function") {
    try {
      const displayed = gridApi.getAllDisplayedColumns();
      if (displayed?.length) {
        return displayed
          .map((col) => col.getColDef())
          .filter((c) => c.field && c.headerName);
      }
    } catch { /* fall through */ }
  }
  return (staticCols || []).filter((c) => c.field && c.headerName && !c.hide);
};

const looksLikeMoneyColumn = (field, headerName) => {
  const h = `${headerName || ""} ${field || ""}`.toLowerCase();
  return /amount|revenue|total|price|cost|balance|paid|\$|fee/.test(h);
};

const thinBorder = {
  top: { style: "thin", color: { argb: "FFE2E8F0" } },
  bottom: { style: "thin", color: { argb: "FFE2E8F0" } },
  left: { style: "thin", color: { argb: "FFE2E8F0" } },
  right: { style: "thin", color: { argb: "FFE2E8F0" } },
};

/**
 * Export grid data to Excel (.xlsx) with formatting (headers, zebra rows, currency, filter, freeze).
 * @param {Array} rowData - Array of row objects
 * @param {Array} columns - AG Grid column defs (uses headerName + field)
 * @param {string} fileName - File name without extension
 * @param {object} [gridApi] - Optional AG Grid API for runtime column visibility
 */
export const exportToExcel = async (rowData, columns, fileName, gridApi) => {
  if (!rowData || rowData.length === 0) return;

  const visibleCols = resolveVisibleColumns(gridApi, columns);
  const headers = visibleCols.map((c) => c.headerName);
  const LARGE_THRESHOLD = 50000;
  const isLarge = rowData.length > LARGE_THRESHOLD;

  const moneyFlags = visibleCols.map((c) => looksLikeMoneyColumn(c.field, c.headerName));

  const wb = new ExcelJS.Workbook();
  wb.creator = "DataVision";
  const ws = wb.addWorksheet("Sales Data", {
    views: [{ state: "frozen", ySplit: 1 }],
    properties: { defaultRowHeight: 18 },
  });

  const headerRow = ws.getRow(1);
  headers.forEach((h, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = h;
    cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1A365D" },
    };
    cell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
    cell.border = thinBorder;
  });
  headerRow.height = 24;

  const altFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF7FAFC" } };

  rowData.forEach((row, ri) => {
    const dataRow = ws.getRow(ri + 2);
    const rowValues = [null];
    visibleCols.forEach((c, ci) => {
      const val = row[c.field];
      if (moneyFlags[ci] && val != null && val !== "" && !Number.isNaN(Number(val))) {
        rowValues.push(typeof val === "number" ? val : parseFloat(val));
      } else if (typeof val === "number") {
        rowValues.push(val);
      } else {
        rowValues.push(val ?? "");
      }
    });
    dataRow.values = rowValues;

    if (!isLarge) {
      dataRow.eachCell({ includeEmpty: true }, (cell, colNum) => {
        if (colNum < 1) return;
        const ci = colNum - 1;
        if (moneyFlags[ci]) cell.numFmt = '"$"#,##0.00';
        cell.border = thinBorder;
        if (ri % 2 === 1) cell.fill = altFill;
      });
    }
  });

  if (!isLarge) {
    visibleCols.forEach((c, i) => {
      const col = ws.getColumn(i + 1);
      let max = String(headers[i] ?? "").length;
      const sample = rowData.slice(0, 500);
      sample.forEach((row) => {
        max = Math.max(max, String(row[c.field] ?? "").length);
      });
      col.width = Math.min(Math.max(max * 0.95 + 2.5, 11), 52);
    });
  }

  if (headers.length > 0) {
    ws.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: headers.length },
    };
  }

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fileName}-${dayjs().format("YYYY-MM-DD")}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
};

const normalizeExportValue = (value, type) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" && value.trim().toLowerCase() === "nan") return "";
  if (type === "datetimeText") {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD 00:00:00") : value;
  }
  if (type === "excelDate") {
    const parsed = dayjs(value);
    // Keep as plain date text to avoid timezone shifts (e.g. previous-day 20:00:00 in UTC-4).
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : value;
  }
  return value;
};

const TEMPLATE_FIELD_ALIASES = {
  net_profit_loss_per_mail: ["net_pl_m"],
  net_pl_m: ["net_profit_loss_per_mail"],
  breakeven_aov: ["be_aov", "be_aov1"],
  long_term_aov: ["lt_aov", "lt_aov1"],
  be_aov1: ["be_aov", "breakeven_aov"],
  lt_aov1: ["lt_aov", "long_term_aov"],
};

const resolveTemplateFieldValue = (row, field) => {
  if (!row || !field) return undefined;
  const direct = row[field];
  if (direct !== undefined && direct !== null && direct !== "") return direct;
  const aliases = TEMPLATE_FIELD_ALIASES[field] || [];
  for (const key of aliases) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return direct;
};

const EXCEL_COL_LETTERS = Array.from({ length: 80 }, (_, i) => {
  let n = i + 1;
  let s = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
});

const CRC32_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[i] = c >>> 0;
  }
  return table;
})();

const crc32Update = (crc, bytes) => {
  let c = crc;
  for (let i = 0; i < bytes.length; i += 1) {
    c = CRC32_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  }
  return c >>> 0;
};

const stripXmlControlChars = (text) => {
  let out = "";
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (code < 32 && code !== 9 && code !== 10 && code !== 13) continue;
    out += text[i];
  }
  return out;
};

const xmlEscape = (value) =>
  stripXmlControlChars(String(value))
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const zipU16 = (n) => Uint8Array.of(n & 255, (n >>> 8) & 255);
const zipU32 = (n) =>
  Uint8Array.of(n & 255, (n >>> 8) & 255, (n >>> 16) & 255, (n >>> 24) & 255);

const zipLocalHeader = (nameBytes, crc, size) => {
  const header = new Uint8Array(30 + nameBytes.length);
  header.set([0x50, 0x4b, 0x03, 0x04, 20, 0], 0);
  header.set(zipU32(crc), 14);
  header.set(zipU32(size), 18);
  header.set(zipU32(size), 22);
  header.set(zipU16(nameBytes.length), 26);
  header.set(nameBytes, 30);
  return header;
};

const zipCentralHeader = (nameBytes, crc, size, offset) => {
  const header = new Uint8Array(46 + nameBytes.length);
  header.set([0x50, 0x4b, 0x01, 0x02, 20, 0, 20, 0], 0);
  header.set(zipU32(crc), 16);
  header.set(zipU32(size), 20);
  header.set(zipU32(size), 24);
  header.set(zipU16(nameBytes.length), 28);
  header.set(zipU32(offset), 42);
  header.set(nameBytes, 46);
  return header;
};

const encodeUtf8 = (text) => new TextEncoder().encode(text);

const cellXml = (colLetter, rowNum, value) => {
  if (value === null || value === undefined || value === "") return "";
  const ref = `${colLetter}${rowNum}`;
  if (typeof value === "number" && Number.isFinite(value)) {
    return `<c r="${ref}"><v>${value}</v></c>`;
  }
  return `<c r="${ref}" t="inlineStr"><is><t>${xmlEscape(value)}</t></is></c>`;
};

const rowXml = (rowNum, values, colLetters) => {
  let cells = "";
  for (let i = 0; i < values.length; i += 1) {
    cells += cellXml(colLetters[i], rowNum, values[i]);
  }
  return cells ? `<row r="${rowNum}">${cells}</row>` : "";
};

const XLSX_CONTENT_TYPES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
</Types>`;

const XLSX_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

const XLSX_WORKBOOK = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets><sheet name="nal_salesdump" sheetId="1" r:id="rId1"/></sheets>
</workbook>`;

const XLSX_WORKBOOK_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
</Relationships>`;

const yieldToUi = () => new Promise((resolve) => setTimeout(resolve, 0));

/** Stream an xlsx (ZIP/STORE + sheet XML). No per-cell object graph. */
export const buildTemplateExcelBlob = async (rowData, template) => {
  if (!rowData || rowData.length === 0) return null;
  const encoder = new TextEncoder();
  const colLetters = template.columns.map((_, i) => EXCEL_COL_LETTERS[i] || "A");
  const sheetParts = [];
  let sheetSize = 0;
  let sheetCrc = 0xffffffff;

  const pushSheet = (text) => {
    const bytes = encoder.encode(text);
    sheetParts.push(bytes);
    sheetSize += bytes.length;
    sheetCrc = crc32Update(sheetCrc, bytes);
  };

  pushSheet(
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>'
  );
  pushSheet(
    rowXml(
      1,
      template.columns.map(([header]) => header ?? ""),
      colLetters
    )
  );

  const batch = 2500;
  for (let i = 0; i < rowData.length; i += batch) {
    let xml = "";
    const end = Math.min(i + batch, rowData.length);
    for (let r = i; r < end; r += 1) {
      const values = template.columns.map(([, field, type]) =>
        normalizeExportValue(resolveTemplateFieldValue(rowData[r], field), type)
      );
      xml += rowXml(r + 2, values, colLetters);
    }
    pushSheet(xml);
    if (rowData.length > 20000) await yieldToUi();
  }
  pushSheet("</sheetData></worksheet>");
  sheetCrc = (sheetCrc ^ 0xffffffff) >>> 0;

  const workbookXml = XLSX_WORKBOOK.replace(
    "nal_salesdump",
    xmlEscape((template.sheetName || "Sheet1").slice(0, 31))
  );

  const files = [
    { name: "[Content_Types].xml", data: encodeUtf8(XLSX_CONTENT_TYPES) },
    { name: "_rels/.rels", data: encodeUtf8(XLSX_RELS) },
    { name: "xl/workbook.xml", data: encodeUtf8(workbookXml) },
    { name: "xl/_rels/workbook.xml.rels", data: encodeUtf8(XLSX_WORKBOOK_RELS) },
    { name: "xl/worksheets/sheet1.xml", parts: sheetParts, size: sheetSize, crc: sheetCrc },
  ];

  const zipParts = [];
  const centrals = [];
  let offset = 0;
  for (const file of files) {
    const nameBytes = encodeUtf8(file.name);
    const size = file.size ?? file.data.length;
    const crc =
      file.crc ?? ((crc32Update(0xffffffff, file.data) ^ 0xffffffff) >>> 0);
    const local = zipLocalHeader(nameBytes, crc, size);
    zipParts.push(local);
    if (file.parts) zipParts.push(...file.parts);
    else zipParts.push(file.data);
    centrals.push(zipCentralHeader(nameBytes, crc, size, offset));
    offset += local.length + size;
  }
  const centralStart = offset;
  zipParts.push(...centrals);
  const centralSize = centrals.reduce((sum, part) => sum + part.length, 0);
  const eocd = new Uint8Array(22);
  eocd.set([0x50, 0x4b, 0x05, 0x06], 0);
  eocd.set(zipU16(files.length), 8);
  eocd.set(zipU16(files.length), 10);
  eocd.set(zipU32(centralSize), 12);
  eocd.set(zipU32(centralStart), 16);
  zipParts.push(eocd);

  return new Blob(zipParts, {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export const downloadExcelBlob = (blob, fileName) => {
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fileName}-${dayjs().format("YYYY-MM-DD")}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
};

export const exportTemplateToExcel = async (rowData, template, fileName) => {
  if (!rowData || rowData.length === 0) return;

  // Large dumps: stream XML+ZIP. SheetJS/ExcelJS build one object per cell and stall.
  if (rowData.length > 20000) {
    downloadExcelBlob(await buildTemplateExcelBlob(rowData, template), fileName);
    return;
  }

  const wb = new ExcelJS.Workbook();
  wb.creator = "DataVision";
  const ws = wb.addWorksheet(template.sheetName);

  const headers = template.columns.map(([header]) => header ?? "");
  ws.addRow(headers);

  rowData.forEach((row) => {
    ws.addRow(
      template.columns.map(([, field, type]) =>
        normalizeExportValue(resolveTemplateFieldValue(row, field), type)
      )
    );
  });

  template.columns.forEach(([, field], idx) => {
    const col = ws.getColumn(idx + 1);
    let max = String(headers[idx] ?? "").length;
    rowData.slice(0, 500).forEach((row) => {
      max = Math.max(max, String(resolveTemplateFieldValue(row, field) ?? "").length);
    });
    col.width = Math.min(Math.max(max * 0.95 + 2.5, 8), 42);
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fileName}-${dayjs().format("YYYY-MM-DD")}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
};

const csvEscape = (value) => {
  if (value === null || value === undefined) return "";
  const stringValue = value instanceof Date
    ? dayjs(value).format("YYYY-MM-DD")
    : String(value);
  if (/[",\r\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

export const exportTemplateToCsv = (rowData, template, fileName) => {
  if (!rowData || rowData.length === 0) return;

  const headers = template.columns.map(([header]) => header ?? "");
  const lines = [
    headers.map(csvEscape).join(","),
    ...rowData.map((row) =>
      template.columns
        .map(([, field, type]) => csvEscape(normalizeExportValue(resolveTemplateFieldValue(row, field), type)))
        .join(",")
    ),
  ];

  const blob = new Blob([lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fileName}-${dayjs().format("YYYY-MM-DD")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Export grid data to PDF (merged stock / dump / mailing tabs — single table).
 * @param {Array} rowData - Array of row objects
 * @param {Array} columns - AG Grid column defs (uses headerName + field)
 * @param {string} fileName - File name without extension
 * @param {string} title - Title to display on the PDF
 * @param {object} [gridApi] - Optional AG Grid API for runtime column visibility
 */
export const exportToPDF = (rowData, columns, fileName, title = "", gridApi) => {
  if (!rowData || rowData.length === 0) return;

  const visibleCols = resolveVisibleColumns(gridApi, columns);
  const headers = visibleCols.map((c) => c.headerName);
  const data = rowData.map((row) =>
    visibleCols.map((c) => {
      const val = row[c.field];
      if (typeof val === "number") {
        return val.toLocaleString("en-US", { maximumFractionDigits: 2 });
      }
      if (typeof val === "boolean") return val ? "Yes" : "No";
      return val ?? "";
    })
  );

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

  if (title) {
    doc.setFontSize(14);
    doc.text(title, 40, 30);
  }
  doc.setFontSize(9);
  doc.text(`Generated: ${dayjs().format("MMM DD, YYYY HH:mm")}`, 40, title ? 48 : 30);

  autoTable(doc, {
    head: [headers],
    body: data,
    startY: title ? 58 : 40,
    styles: { fontSize: 7, cellPadding: 3 },
    headStyles: { fillColor: [24, 144, 255], fontSize: 7 },
    margin: { left: 20, right: 20 },
    theme: "grid",
  });

  const dateStr = dayjs().format("YYYY-MM-DD");
  doc.save(`${fileName}-${dateStr}.pdf`);
};

/** USD in PDF tables — matches app `formatCurrency` ($ prefix). */
const pdfMoneyCell = (n) => formatCurrency(n);
const pdfMoneyTotal = (n) => formatCurrency(n);

const revenueByClient = (records) => {
  const m = {};
  records.forEach((r) => {
    const c = r.client || "Unknown";
    m[c] = (m[c] || 0) + (r.amount || 0);
  });
  return m;
};

/** PDF doughnut palette — saturated enough for print, distinct from neighbors. */
const PDF_DOUGHNUT_COLORS = [
  "#2563eb",
  "#059669",
  "#d97706",
  "#db2777",
  "#7c3aed",
  "#0d9488",
  "#dc2626",
  "#ca8a04",
  "#4f46e5",
  "#16a34a",
];

/** Canvas background + anti-aliasing for export charts. */
function prepPdfChartCanvas(w, h) {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.fillStyle = "#f8fafc";
    ctx.fillRect(0, 0, w, h);
  }
  return canvas;
}

function makePdfDoughnutCenterPlugin(hasData, values) {
  return {
    id: "pdfDoughnutCenter",
    afterDraw(chart) {
      if (!hasData) return;
      const meta = chart.getDatasetMeta(0);
      const arc = meta?.data?.[0];
      if (!arc) return;
      const { x, y } = arc;
      const ctx = chart.ctx;
      const total = values.reduce((s, v) => s + (Number(v) || 0), 0);
      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#0f172a";
      ctx.font = "600 16px 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif";
      ctx.fillText(formatCurrency(total), x, y - 6);
      ctx.fillStyle = "#64748b";
      ctx.font = "500 10px 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif";
      ctx.fillText("Total", x, y + 10);
      ctx.restore();
    },
  };
}

const doughnutPngFromRevenueMap = async (revenueMap) => {
  const { default: Chart } = await import("chart.js/auto");
  const entries = Object.entries(revenueMap)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  const hasData = entries.length > 0;
  const labels = hasData ? entries.map(([k]) => k) : ["No data"];
  const values = hasData ? entries.map(([, v]) => v) : [1];
  const placeholderBg = hasData ? null : ["#e2e8f0"];
  const W = 780;
  const H = 320;
  const canvas = prepPdfChartCanvas(W, H);
  const chart = new Chart(canvas.getContext("2d"), {
    type: "doughnut",
    plugins: [makePdfDoughnutCenterPlugin(hasData, values)],
    data: {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: placeholderBg || labels.map((_, i) => PDF_DOUGHNUT_COLORS[i % PDF_DOUGHNUT_COLORS.length]),
          borderColor: "#f8fafc",
          borderWidth: 3,
          hoverOffset: 0,
        },
      ],
    },
    options: {
      animation: false,
      responsive: false,
      cutout: "64%",
      layout: { padding: { top: 8, bottom: 8, left: 12, right: 14 } },
      plugins: {
        datalabels: { display: false },
        title: { display: false },
        legend: {
          position: "right",
          align: "center",
          maxWidth: 280,
          labels: {
            color: "#475569",
            font: { size: 10, family: "'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif" },
            boxWidth: 10,
            boxHeight: 10,
            padding: 8,
            usePointStyle: true,
            pointStyle: "circle",
            generateLabels: (chart) => {
              const ds = chart.data.datasets[0];
              const colors = Array.isArray(ds.backgroundColor) ? ds.backgroundColor : [];
              const border = ds.borderColor || "#f8fafc";
              const bw = typeof ds.borderWidth === "number" ? ds.borderWidth : 3;
              return chart.data.labels.map((label, i) => {
                const v = Number(ds.data[i]) || 0;
                const short = String(label).length > 32 ? `${String(label).slice(0, 30)}…` : label;
                const isPlaceholder = !hasData && i === 0;
                return {
                  text: isPlaceholder ? "No revenue in period" : `${short}  ·  ${formatCurrency(v)}`,
                  fillStyle: colors[i] || "#cbd5e0",
                  strokeStyle: border,
                  lineWidth: bw,
                  hidden: !chart.getDataVisibility(i),
                  index: i,
                };
              });
            },
          },
        },
        tooltip: { enabled: false },
      },
    },
  });
  chart.update("none");
  const url = canvas.toDataURL("image/png", 1);
  chart.destroy();
  return url;
};

const PIE_CHART_COLORS = [
  "#1565C0", "#43A047", "#FFB300", "#8E24AA", "#00ACC1",
  "#F4511E", "#5C6BC0", "#D81B60", "#00897B", "#C0CA33",
];

async function piePngFromRevenueMap(revenueMap, title = "Sales Revenue") {
  const { default: ChartLib } = await import("chart.js/auto");
  const entries = Object.entries(revenueMap)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  if (!entries.length) return null;
  const labels = entries.map(([k]) => k);
  const values = entries.map(([, v]) => v);
  const total = values.reduce((s, v) => s + v, 0);
  const DPR = 3;
  const canvas = document.createElement("canvas");
  canvas.width = 480;
  canvas.height = 320;
  const ctx = canvas.getContext("2d");
  const chart = new ChartLib(ctx, {
    type: "pie",
    data: {
      labels,
      datasets: [{
        data: values,
        backgroundColor: labels.map((_, i) => PIE_CHART_COLORS[i % PIE_CHART_COLORS.length]),
        borderColor: "#ffffff",
        borderWidth: 2,
      }],
    },
    options: {
      animation: false,
      responsive: false,
      devicePixelRatio: DPR,
      layout: { padding: { top: 28, bottom: 8, left: 8, right: 8 } },
      plugins: {
        title: {
          display: true,
          text: title,
          font: { size: 13, weight: "bold", family: "Arial, sans-serif" },
          color: "#000000",
          padding: { bottom: 10 },
        },
        datalabels: {
          formatter: (val) => {
            const pct = total > 0 ? ((val / total) * 100).toFixed(0) : 0;
            return Number(pct) >= 3 ? `${pct}%` : "";
          },
          color: "#ffffff",
          font: { size: 11, weight: "bold", family: "Arial, sans-serif" },
        },
        legend: {
          position: "right",
          labels: {
            color: "#000000",
            font: { size: 9, family: "Arial, sans-serif" },
            boxWidth: 10,
            boxHeight: 10,
            padding: 6,
            usePointStyle: true,
            pointStyle: "rectRounded",
          },
        },
        tooltip: { enabled: false },
      },
    },
  });
  chart.update("none");
  const url = canvas.toDataURL("image/png", 1);
  chart.destroy();
  return url;
}

/**
 * Daily Sales PDF aligned to the reference "Sales Report" pack: page 1 = header + summary + two revenue doughnuts;
 * following pages = order line items (grid). Does not include per-brand Sublytics gateway pages from the 12-page reference.
 */
export const exportDailySalesReportPdf = async ({
  rowData,
  columns,
  fileName = "daily-sales-report",
  gridApi,
  summaryRecords,
  dateRange,
  responsible = "",
}) => {
  if (!rowData || rowData.length === 0) return;
  if (
    !summaryRecords ||
    !dateRange?.[0] ||
    !dateRange?.[1]
  ) {
    exportToPDF(rowData, columns, fileName, "Daily Sales Report", gridApi);
    return;
  }

  const visibleCols = resolveVisibleColumns(gridApi, columns);
  const headers = visibleCols.map((c) => c.headerName);
  const data = rowData.map((row) =>
    visibleCols.map((c) => {
      const val = row[c.field];
      if (typeof val === "number") {
        return val.toLocaleString("en-US", { maximumFractionDigits: 2 });
      }
      if (typeof val === "boolean") return val ? "Yes" : "No";
      return val ?? "";
    })
  );

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const dateOfReport = dayjs().format("MMM D, YYYY");
  const periodFrom = dateRange[0].format("MMM D, YYYY");
  const periodTo = dateRange[1].format("MMM D, YYYY");

  let y = 36;
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, pageW, 58, "F");
  doc.setDrawColor(45, 212, 191);
  doc.setLineWidth(2.5);
  doc.line(0, 58, pageW, 58);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Daily Sales Report", 40, 38);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(203, 213, 225);
  doc.text(`Generated ${dateOfReport}`, pageW - 40, 38, { align: "right" });

  doc.setTextColor(51, 65, 85);
  y = 74;
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text("Reporting period", 40, y);
  y += 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);
  doc.text(`${periodFrom}  →  ${periodTo}`, 40, y);
  y += 12;
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text("Full calendar days in US/Eastern (America/New_York), inclusive.", 40, y);
  y += 14;
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.text(`Responsible: ${responsible || "—"}`, 40, y);
  y += 22;

  const clientMap = groupByClient(summaryRecords);
  const clientNames = Object.keys(clientMap).sort();
  const webSubOrders = summaryRecords.filter(
    (r) => r.source === "Sublytics" || r.source === "Wordpress"
  );
  const ttOrders = summaryRecords.filter((r) => r.source === "TigerTech");

  const head = [
    [
      { content: "", styles: { fillColor: [248, 250, 252] } },
      {
        content: "Website & Sublytics",
        colSpan: 3,
        styles: {
          fillColor: [224, 231, 255],
          textColor: [30, 41, 59],
          fontStyle: "bold",
          halign: "center",
          fontSize: 10,
        },
      },
      { content: "", styles: { fillColor: [248, 250, 252] } },
      {
        content: "TigerTech",
        colSpan: 3,
        styles: {
          fillColor: [204, 251, 241],
          textColor: [17, 94, 89],
          fontStyle: "bold",
          halign: "center",
          fontSize: 10,
        },
      },
    ],
    [
      "",
      "Client",
      "Orders",
      "Revenue",
      "",
      "Client",
      "Orders",
      "Revenue",
    ],
  ];

  const body = clientNames.map((client) => {
    const clientWS = webSubOrders.filter((r) => r.client === client);
    const clientTT = ttOrders.filter((r) => r.client === client);
    const wsCount = clientWS.length;
    const wsRev = clientWS.reduce((s, r) => s + (r.amount || 0), 0);
    const ttCount = clientTT.length;
    const ttRev = clientTT.reduce((s, r) => s + (r.amount || 0), 0);
    return ["", client, wsCount, pdfMoneyCell(wsRev), "", client, ttCount, pdfMoneyCell(ttRev)];
  });

  const totalWSCount = webSubOrders.length;
  const totalWSRev = webSubOrders.reduce((s, r) => s + (r.amount || 0), 0);
  const totalTTCount = ttOrders.length;
  const totalTTRev = ttOrders.reduce((s, r) => s + (r.amount || 0), 0);
  body.push([
    "",
    "Total",
    totalWSCount,
    pdfMoneyTotal(totalWSRev),
    "",
    "Total",
    totalTTCount,
    pdfMoneyTotal(totalTTRev),
  ]);

  const summaryBodyLastIndex = body.length - 1;

  autoTable(doc, {
    startY: y,
    head,
    body,
    styles: {
      fontSize: 9,
      cellPadding: { top: 6, bottom: 6, left: 8, right: 8 },
      textColor: [30, 41, 59],
      lineColor: [226, 232, 240],
      lineWidth: 0.35,
    },
    headStyles: {
      fillColor: [248, 250, 252],
      textColor: [51, 65, 85],
      fontSize: 8.5,
      fontStyle: "bold",
      cellPadding: { top: 7, bottom: 7, left: 8, right: 8 },
    },
    alternateRowStyles: { fillColor: [255, 255, 255] },
    margin: { left: 40, right: 40 },
    theme: "plain",
    columnStyles: {
      0: { cellWidth: 8 },
      4: { cellWidth: 8 },
      2: { halign: "right" },
      3: { halign: "right" },
      6: { halign: "right" },
      7: { halign: "right" },
    },
    didParseCell: (hookData) => {
      if (hookData.section === "body" && hookData.row.index === summaryBodyLastIndex) {
        hookData.cell.styles.fontStyle = "bold";
        hookData.cell.styles.textColor = [15, 23, 42];
        hookData.cell.styles.fillColor = [241, 245, 249];
        hookData.cell.styles.fontSize = 9.5;
      }
    },
  });

  const tableBottom =
    typeof doc.lastAutoTable?.finalY === "number" ? doc.lastAutoTable.finalY + 20 : y + 120;

  let chartY = tableBottom;
  if (chartY > 320) {
    doc.addPage();
    chartY = 48;
  }

  const wsMap = revenueByClient(webSubOrders);
  const ttMap = revenueByClient(ttOrders);
  const [pngWs, pngTt] = await Promise.all([
    doughnutPngFromRevenueMap(wsMap),
    doughnutPngFromRevenueMap(ttMap),
  ]);

  doc.setTextColor(15, 23, 42);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Revenue mix by client", 40, chartY);
  chartY += 16;

  const gap = 14;
  const cardPad = 10;
  const labelBand = 20;
  const imgW = (pageW - 80 - gap) / 2;
  const imgH = Math.round((imgW * 320) / 780);
  const cardW = imgW + cardPad * 2;
  const cardH = labelBand + imgH + cardPad * 2;

  const drawChartCard = (leftX, topY, subtitle) => {
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.6);
    if (typeof doc.roundedRect === "function") {
      doc.roundedRect(leftX, topY, cardW, cardH, 8, 8, "FD");
    } else {
      doc.rect(leftX, topY, cardW, cardH, "FD");
    }
    doc.setTextColor(71, 85, 105);
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.text(subtitle, leftX + cardPad, topY + 13);
  };

  const rowTop = chartY;
  drawChartCard(40, rowTop, "Website & Sublytics");
  drawChartCard(40 + cardW + gap, rowTop, "TigerTech");
  const imgTop = rowTop + labelBand;
  doc.addImage(pngWs, "PNG", 40 + cardPad, imgTop, imgW, imgH);
  doc.addImage(pngTt, "PNG", 40 + cardW + gap + cardPad, imgTop, imgW, imgH);

  doc.addPage();
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, pageW, 40, "F");
  doc.setDrawColor(45, 212, 191);
  doc.setLineWidth(2);
  doc.line(0, 40, pageW, 40);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.text("Order line items", 40, 26);
  doc.setTextColor(203, 213, 225);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(`Exported ${dayjs().format("MMM D, YYYY · h:mm A")}`, pageW - 40, 26, {
    align: "right",
  });

  autoTable(doc, {
    startY: 52,
    head: [headers],
    body: data,
    showHead: "everyPage",
    styles: {
      fontSize: 8,
      cellPadding: { top: 5, bottom: 5, left: 6, right: 6 },
      textColor: [30, 41, 59],
      lineColor: [226, 232, 240],
      lineWidth: 0.35,
    },
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: [248, 250, 252],
      fontSize: 8,
      fontStyle: "bold",
      cellPadding: { top: 8, bottom: 8, left: 6, right: 6 },
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: 32, right: 32, top: 48 },
    theme: "plain",
  });

  const dateStr = dayjs().format("YYYY-MM-DD");
  doc.save(`${fileName}-${dateStr}.pdf`);
};

const fmtNum = (v) => (typeof v === "number" ? Math.round(v * 100) / 100 : v ?? "");

const autoColWidths = (ws) => {
  const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
  const colCount = range.e.c + 1;
  ws["!cols"] = Array.from({ length: colCount }, (_, i) => {
    let maxLen = 8;
    for (let r = range.s.r; r <= range.e.r; r++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c: i })];
      if (cell && cell.v != null) {
        maxLen = Math.max(maxLen, String(cell.v).length);
      }
    }
    return { wch: Math.min(maxLen + 3, 45) };
  });
  return ws;
};

const groupByClient = (records) => {
  const map = {};
  records.forEach((r) => {
    const c = r.client || "Unknown";
    if (!map[c]) map[c] = [];
    map[c].push(r);
  });
  return map;
};

const buildAllClientsSheet = (allRecords, dateRange) => {
  const rows = [];
  const dateOfReport = dayjs().format("D/M/YYYY");
  const periodFrom = dateRange?.[0]?.format("D/M/YYYY") || dateOfReport;
  const periodTo = dateRange?.[1]?.format("D/M/YYYY") || periodFrom;

  rows.push(["Date of the report:", dateOfReport]);
  rows.push(["Transaction from:", periodFrom, "To:", periodTo]);
  rows.push(["Responsible:", ""]);
  rows.push([]);

  const clientMap = groupByClient(allRecords);
  const clientNames = Object.keys(clientMap).sort();

  const webSubOrders = allRecords.filter(
    (r) => r.source === "Sublytics" || r.source === "Wordpress"
  );
  const ttOrders = allRecords.filter((r) => r.source === "TigerTech");
  const nxOrders = allRecords.filter((r) => r.source === "NexRep");
  const rpmOrders = allRecords.filter((r) => r.source === "RPM");

  rows.push([
    "",
    "WEBSITE AND SUBLYTICS",
    "",
    "",
    "",
    "TIGERTECH",
    "",
    "",
    "",
    "NEXREP",
    "",
    "",
    "",
    "RPM",
  ]);
  rows.push([
    "",
    "Clients",
    "Total No of Orders",
    "Sales Revenue",
    "",
    "Clients",
    "Total No of Orders",
    "Sales Revenue",
    "",
    "Clients",
    "Total No of Orders",
    "Sales Revenue",
    "",
    "Clients",
    "Total No of Orders",
    "Sales Revenue",
  ]);

  clientNames.forEach((client) => {
    const clientWS = webSubOrders.filter((r) => r.client === client);
    const clientTT = ttOrders.filter((r) => r.client === client);
    const clientNX = nxOrders.filter((r) => r.client === client);
    const clientRPM = rpmOrders.filter((r) => r.client === client);
    const wsCount = clientWS.length;
    const wsRev = clientWS.reduce((s, r) => s + (r.amount || 0), 0);
    const ttCount = clientTT.length;
    const ttRev = clientTT.reduce((s, r) => s + (r.amount || 0), 0);
    const nxCount = clientNX.length;
    const nxRev = clientNX.reduce((s, r) => s + (r.amount || 0), 0);
    const rpmCount = clientRPM.length;
    const rpmRev = clientRPM.reduce((s, r) => s + (r.amount || 0), 0);
    rows.push([
      "", client, wsCount, fmtNum(wsRev), "",
      client, ttCount, fmtNum(ttRev), "",
      client, nxCount, fmtNum(nxRev), "",
      client, rpmCount, fmtNum(rpmRev),
    ]);
  });

  const totalWSCount = webSubOrders.length;
  const totalWSRev = webSubOrders.reduce((s, r) => s + (r.amount || 0), 0);
  const totalTTCount = ttOrders.length;
  const totalTTRev = ttOrders.reduce((s, r) => s + (r.amount || 0), 0);
  const totalNXCount = nxOrders.length;
  const totalNXRev = nxOrders.reduce((s, r) => s + (r.amount || 0), 0);
  const totalRPMCount = rpmOrders.length;
  const totalRPMRev = rpmOrders.reduce((s, r) => s + (r.amount || 0), 0);
  rows.push([
    "", "Total", totalWSCount, fmtNum(totalWSRev), "",
    "Total", totalTTCount, fmtNum(totalTTRev), "",
    "Total", totalNXCount, fmtNum(totalNXRev), "",
    "Total", totalRPMCount, fmtNum(totalRPMRev),
  ]);

  return rows;
};

const getSublyticsBreakdown = (sublyticsRecords) => {
  const merchants = {};
  sublyticsRecords.forEach((rec) => {
    const raw = rec.rawData || {};
    const merchantId = raw.merchant_id || raw.gateway_name || raw.merchant || "";
    const key = merchantId || "Other";
    if (!merchants[key]) merchants[key] = { count: 0, revenue: 0 };
    merchants[key].count++;
    merchants[key].revenue += rec.amount || 0;
  });
  return merchants;
};

const MERCHANT_KEY_ALIASES = {
  "Auth.Net #1": ["Auth.Net #1", "auth_net_1", "auth.net #1", "authnet1", "Auth.Net"],
  "Auth.Net #2": ["Auth.Net #2", "auth_net_2", "auth.net #2", "authnet2"],
  "Check": ["Check", "check", "Check gateway", "check_gateway"],
  "Cash": ["Cash", "cash", "Cash gateway", "cash_gateway"],
  "NMI Payarc": ["NMI Payarc", "nmi", "NMI", "nmi_payarc", "NMI PayArc"],
};

const resolveMerchantCount = (breakdown, merchantName) => {
  const aliases = MERCHANT_KEY_ALIASES[merchantName] || [merchantName];
  for (const alias of aliases) {
    if (breakdown[alias]) return breakdown[alias];
  }
  return { count: 0, revenue: 0 };
};

const classifyAuthTransaction = (t) => {
  const email = (t.transaction_details?.customer?.email || t.email || "").toUpperCase();
  const status = (t.transactionStatus || "").toLowerCase();
  const isSettled =
    status.includes("settled") ||
    status === "capturedpendingsettlement" ||
    status === "settledsuccessfully";
  return { email, isSettled };
};

const getGatewayMatches = (records) => {
  const auth1Settled = [];
  const auth1Unsettled = [];
  const auth2Settled = [];
  const auth2Unsettled = [];
  const nmiSettled = [];
  const nmiUnsettled = [];

  records.forEach((rec) => {
    const raw = rec.rawData || {};
    const merchantKey = (raw.merchant_id || raw.gateway_name || raw.merchant || "").toLowerCase();
    const isAuth2 = merchantKey.includes("2") || merchantKey.includes("auth_net_2");

    (raw.real_auth_transactions || []).forEach((t) => {
      const { email, isSettled } = classifyAuthTransaction(t);
      if (isAuth2) {
        (isSettled ? auth2Settled : auth2Unsettled).push(email);
      } else {
        (isSettled ? auth1Settled : auth1Unsettled).push(email);
      }
    });

    (raw.real_nmi_transactions || []).forEach((t) => {
      const email = (t.email || "").toUpperCase();
      const condition = (t.condition || "").toLowerCase();
      if (condition === "complete" || condition === "settled") {
        nmiSettled.push(email);
      } else {
        nmiUnsettled.push(email);
      }
    });
  });

  return { auth1Settled, auth1Unsettled, auth2Settled, auth2Unsettled, nmiSettled, nmiUnsettled };
};

const BRAND_PREFIX_MAP = {
  "Nutrinova": "NN",
  "House of Man": "HOM",
  "Gold Pharma": "GP",
  "Max Vital": "MV",
  "Health Spark": "HS",
  "U Power": "UP",
};

const buildBrandSheet = (records, brandName, clientName, dateRange) => {
  const rows = [];
  const dateOfReport = dayjs().format("D/M/YYYY");
  const periodFrom = dateRange?.[0]?.format("D/M/YYYY") || dateOfReport;
  const periodTo = dateRange?.[1]?.format("D/M/YYYY") || periodFrom;
  const prefix = BRAND_PREFIX_MAP[brandName] || brandName.slice(0, 3).toUpperCase();

  rows.push(["Date of the report:", dateOfReport]);
  rows.push(["Transaction from:", periodFrom, "To:", periodTo]);
  rows.push(["Responsible:", ""]);
  rows.push([]);
  rows.push(["Client:", clientName || brandName]);
  rows.push(["Brand:", brandName]);
  rows.push([]);

  const wpRecords = records.filter((r) => r.source === "Wordpress");
  const subRecords = records.filter((r) => r.source === "Sublytics");
  const ttRecords = records.filter((r) => r.source === "TigerTech");
  const nxRecords = records.filter((r) => r.source === "NexRep");
  const rpmRecords = records.filter((r) => r.source === "RPM");
  const authRecords = records.filter((r) => r.source === "AUTH.NET #1");
  const auth2Records = records.filter((r) => r.source === "AUTH.NET #2");
  const nmiRecords = records.filter((r) => r.source === "NMI Payarc");

  const wpCount = wpRecords.length;
  const wpRev = wpRecords.reduce((s, r) => s + (r.amount || 0), 0);
  const subCount = subRecords.length;
  const subRev = subRecords.reduce((s, r) => s + (r.amount || 0), 0);
  const totalCount = wpCount + subCount;
  const totalRev = wpRev + subRev;

  const merchantBreakdown = getSublyticsBreakdown(subRecords);
  const allMerchantNames = ["Auth.Net #1", "Auth.Net #2", "Check", "Cash", "Money Order", "NMI Payarc"];
  const activeMerchants = allMerchantNames.filter(
    (m) => resolveMerchantCount(merchantBreakdown, m).count > 0
  );
  if (activeMerchants.length === 0 && subCount > 0) {
    const fallbackKeys = Object.keys(merchantBreakdown).filter((k) => k !== "Other");
    if (fallbackKeys.length > 0) activeMerchants.push(...fallbackKeys);
    else activeMerchants.push("Other");
  }

  const activeGateways = [];
  if (authRecords.length > 0) activeGateways.push({ label: `${prefix} - Auth.net #1`, records: authRecords });
  if (auth2Records.length > 0) activeGateways.push({ label: `${prefix} - Auth.net #2`, records: auth2Records });
  if (nmiRecords.length > 0) activeGateways.push({ label: `${prefix} - NMI PayArc`, records: nmiRecords });
  if (wpRecords.length > 0) activeGateways.push({ label: "Stripe (Website)", records: wpRecords });

  // TOTAL section header
  const breakdownGap = 5;
  const totalHeader = ["TOTAL:", "", "", ""];
  const breakdownHeader = ["", "SUBLYTICS BREAKDOWN BY SOURCE:"];
  for (let i = 1; i < activeMerchants.length; i++) breakdownHeader.push("");
  breakdownHeader.push("");

  const gatewayHeader = ["PAYMENT GATEWAYS:"];
  rows.push([...totalHeader, "", ...breakdownHeader, ...gatewayHeader]);

  const totalSubRow = ["-", "Website", "Sublytics", "Total", ""];
  const breakdownSubRow = ["-", ...activeMerchants, "Total"];
  const gatewaySubRow = ["-", ...activeGateways.map((g) => g.label)];
  rows.push([...totalSubRow, ...breakdownSubRow, ...gatewaySubRow]);

  // Orders row
  const merchantCounts = activeMerchants.map((m) => {
    const d = resolveMerchantCount(merchantBreakdown, m);
    return d.count || merchantBreakdown[m]?.count || "";
  });
  const breakdownTotal = activeMerchants.reduce((s, m) => {
    const d = resolveMerchantCount(merchantBreakdown, m);
    return s + (d.count || merchantBreakdown[m]?.count || 0);
  }, 0);
  const gwCounts = activeGateways.map((g) => g.records.length || "");
  rows.push([
    "Total No of orders:", wpCount || "", subCount || "", totalCount,
    "", "Total No of orders:", ...merchantCounts, breakdownTotal || subCount,
    "Total No of orders:", ...gwCounts,
  ]);

  // Revenue row
  const merchantRevs = activeMerchants.map((m) => {
    const d = resolveMerchantCount(merchantBreakdown, m);
    const rev = d.revenue || merchantBreakdown[m]?.revenue || 0;
    return rev ? fmtNum(rev) : "";
  });
  const breakdownRevTotal = activeMerchants.reduce((s, m) => {
    const d = resolveMerchantCount(merchantBreakdown, m);
    return s + (d.revenue || merchantBreakdown[m]?.revenue || 0);
  }, 0);
  const gwRevs = activeGateways.map((g) =>
    g.records.reduce((s, r) => s + (r.amount || 0), 0)
  ).map((v) => v ? fmtNum(v) : "");
  rows.push([
    "Total Sales Revenue:", wpRev ? fmtNum(wpRev) : "", subRev ? fmtNum(subRev) : "", fmtNum(totalRev),
    "", "Total Sales Revenue:", ...merchantRevs, fmtNum(breakdownRevTotal || subRev),
    "Total Sales Revenue:", ...gwRevs,
  ]);
  rows.push([]);

  // TIGERTECH — aligned under Payment Gateways column
  const gwColStart = 5 + activeMerchants.length + 2 + 1;
  const pad = (n) => Array(n).fill("");
  rows.push([...pad(gwColStart), "TIGERTECH"]);
  rows.push([...pad(gwColStart), "-", "-"]);
  const ttCount = ttRecords.length;
  const ttRev = ttRecords.reduce((s, r) => s + (r.amount || 0), 0);
  rows.push([...pad(gwColStart), "Total No of orders:", ttCount]);
  rows.push([...pad(gwColStart), "Total Sales Revenue:", fmtNum(ttRev)]);
  if (nxRecords.length > 0) {
    const nxCount = nxRecords.length;
    const nxRev = nxRecords.reduce((s, r) => s + (r.amount || 0), 0);
    rows.push([]);
    rows.push([...pad(gwColStart), "NEXREP"]);
    rows.push([...pad(gwColStart), "-", "-"]);
    rows.push([...pad(gwColStart), "Total No of orders:", nxCount]);
    rows.push([...pad(gwColStart), "Total Sales Revenue:", fmtNum(nxRev)]);
  }
  if (rpmRecords.length > 0) {
    const rpmCount = rpmRecords.length;
    const rpmRev = rpmRecords.reduce((s, r) => s + (r.amount || 0), 0);
    rows.push([]);
    rows.push([...pad(gwColStart), "RPM"]);
    rows.push([...pad(gwColStart), "-", "-"]);
    rows.push([...pad(gwColStart), "Total No of orders:", rpmCount]);
    rows.push([...pad(gwColStart), "Total Sales Revenue:", fmtNum(rpmRev)]);
  }

  return rows;
};

const buildMatchingSheet = (records, brandName) => {
  const subRecords = records.filter((r) => r.source === "Sublytics");
  const gatewayData = getGatewayMatches(subRecords);

  const hasAuth1 = gatewayData.auth1Settled.length > 0 || gatewayData.auth1Unsettled.length > 0;
  const hasAuth2 = gatewayData.auth2Settled.length > 0 || gatewayData.auth2Unsettled.length > 0;

  if (!hasAuth1 && !hasAuth2) return [];

  const rows = [];

  // Row 1: top-level headers
  const headerRow1 = ["SUBLYTICS", "Match"];
  if (hasAuth2) headerRow1.push("AUTH.NET 2", "Match");
  headerRow1.push("");
  if (hasAuth1) headerRow1.push("PAYMENT GATEWAY");
  if (hasAuth1 && hasAuth2) headerRow1.push("");
  rows.push(headerRow1);

  // Row 2: sub-headers with settled/unsettled labels
  const headerRow2 = hasAuth1 ? ["AUTH.NET 1"] : [""];
  headerRow2.push("Match");
  if (hasAuth2) headerRow2.push("AUTH.NET 2", "Match");
  headerRow2.push("");
  if (hasAuth1) headerRow2.push(`AUTH.NET 1 Settled Transactions`, "", `AUTH.NET 1 Unsettled Transactions`);
  if (hasAuth2) headerRow2.push("", `AUTH.NET 2 Settled Transactions`, "", `AUTH.NET 2 Unsettled Transactions`);
  rows.push(headerRow2);

  const subEmails = subRecords.map((r) => (r.customer_email || "").toUpperCase());

  const maxRows = Math.max(
    subEmails.length,
    hasAuth1 ? Math.max(gatewayData.auth1Settled.length, gatewayData.auth1Unsettled.length) : 0,
    hasAuth2 ? Math.max(gatewayData.auth2Settled.length, gatewayData.auth2Unsettled.length) : 0
  );

  for (let i = 0; i < maxRows; i++) {
    const subEmail = subEmails[i] || "";
    const matchedAuth1 = subEmail && gatewayData.auth1Settled.some((e) => e === subEmail) ? "YES" : subEmail ? "NO" : "";

    const row = [subEmail, matchedAuth1];

    if (hasAuth2) {
      const matchedAuth2 = subEmail && gatewayData.auth2Settled.some((e) => e === subEmail) ? "YES" : subEmail ? "NO" : "";
      row.push(subEmail, matchedAuth2);
    }

    row.push("");

    if (hasAuth1) {
      row.push(gatewayData.auth1Settled[i] || "", "", gatewayData.auth1Unsettled[i] || "");
    }
    if (hasAuth2) {
      row.push("", gatewayData.auth2Settled[i] || "", "", gatewayData.auth2Unsettled[i] || "");
    }

    rows.push(row);
  }

  return rows;
};

const buildWeeklySalesSheet = (allRecords, dateRange) => {
  const rows = [];
  const clientMap = groupByClient(allRecords);
  const clientNames = Object.keys(clientMap).sort();

  const webSubOrders = allRecords.filter(
    (r) => r.source === "Sublytics" || r.source === "Wordpress"
  );

  const dateGrouped = {};
  webSubOrders.forEach((r) => {
    const d = r.date ? r.date.split(" ")[0] : "";
    if (!d) return;
    if (!dateGrouped[d]) dateGrouped[d] = [];
    dateGrouped[d].push(r);
  });

  const sortedDates = Object.keys(dateGrouped).sort();

  sortedDates.forEach((date) => {
    const displayDate = dayjs(date).format("D/M/YYYY");
    rows.push([]);
    rows.push(["", displayDate]);
    rows.push(["", "Clients", "Total No of Orders", "Sales Revenue"]);

    let dayTotalOrders = 0;
    let dayTotalRev = 0;
    clientNames.forEach((client) => {
      const clientDayRecs = dateGrouped[date].filter((r) => r.client === client);
      const count = clientDayRecs.length;
      const rev = clientDayRecs.reduce((s, r) => s + (r.amount || 0), 0);
      dayTotalOrders += count;
      dayTotalRev += rev;
      rows.push(["", client, count || "", rev ? fmtNum(rev) : ""]);
    });
    rows.push(["", "Total", dayTotalOrders, fmtNum(dayTotalRev)]);
  });

  // Summary grid (right side of reference)
  if (sortedDates.length > 0) {
    rows.push([]);
    rows.push([]);

    const summaryHeader = ["", "Total"];
    rows.push(summaryHeader);

    const gridHeader = ["", "Clients", "Total No of Orders", "Sales Revenue"];
    rows.push(gridHeader);

    let grandTotalOrders = 0;
    let grandTotalRev = 0;
    clientNames.forEach((client) => {
      const clientRecs = webSubOrders.filter((r) => r.client === client);
      const count = clientRecs.length;
      const rev = clientRecs.reduce((s, r) => s + (r.amount || 0), 0);
      grandTotalOrders += count;
      grandTotalRev += rev;
      rows.push(["", client, count, fmtNum(rev)]);
    });
    rows.push(["", "Total", grandTotalOrders, fmtNum(grandTotalRev)]);

    rows.push([]);
    const pivotHeader = ["-", ...clientNames, "Total"];
    rows.push(pivotHeader);
    sortedDates.forEach((date) => {
      const displayDate = dayjs(date).format("D/M/YYYY");
      const pivotRow = [displayDate];
      let dateTotal = 0;
      clientNames.forEach((client) => {
        const rev = dateGrouped[date]
          .filter((r) => r.client === client)
          .reduce((s, r) => s + (r.amount || 0), 0);
        dateTotal += rev;
        pivotRow.push(rev ? fmtNum(rev) : 0);
      });
      pivotRow.push(fmtNum(dateTotal));
      rows.push(pivotRow);
    });

    const totalPivotRow = ["Total"];
    let grandTotal = 0;
    clientNames.forEach((client) => {
      const rev = webSubOrders
        .filter((r) => r.client === client)
        .reduce((s, r) => s + (r.amount || 0), 0);
      grandTotal += rev;
      totalPivotRow.push(fmtNum(rev));
    });
    totalPivotRow.push(fmtNum(grandTotal));
    rows.push(totalPivotRow);
  }

  return rows;
};

/**
 * Export full dashboard to a multi-sheet Excel workbook matching the
 * reference Sales Report format.
 *
 * Sheet 1: "Daily Sales Report- All Clients" — summary table
 * Per brand: brand sheet (totals + breakdown) + matching sheet
 */
export const exportDashboardToExcel = (allRecords, dateRange, brandClientMap) => {
  if (!allRecords || allRecords.length === 0) return;

  const wb = XLSX.utils.book_new();

  // Sheet 1: Daily Sales Report - All Clients
  const allClientsRows = buildAllClientsSheet(allRecords, dateRange);
  const wsAll = XLSX.utils.aoa_to_sheet(allClientsRows);
  autoColWidths(wsAll);
  XLSX.utils.book_append_sheet(wb, wsAll, "Daily Sales Report- All Clien");

  // Sheet 2: Weekly Sales Revenue
  const weeklyRows = buildWeeklySalesSheet(allRecords, dateRange);
  if (weeklyRows.length > 0) {
    const wsWeekly = XLSX.utils.aoa_to_sheet(weeklyRows);
    autoColWidths(wsWeekly);
    XLSX.utils.book_append_sheet(wb, wsWeekly, "Weekly Sales Revenue");
  }

  // Per-brand: Brand summary + Customer matching
  const clientMap = groupByClient(allRecords);

  Object.entries(clientMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .forEach(([brand, records]) => {
      const clientName = brandClientMap?.[brand] || brand;
      const safeBrand = brand.replace(/[[\]*?/\\:]/g, "_").slice(0, 31);

      const brandRows = buildBrandSheet(records, brand, clientName, dateRange);
      const wsBrand = XLSX.utils.aoa_to_sheet(brandRows);
      autoColWidths(wsBrand);
      XLSX.utils.book_append_sheet(wb, wsBrand, safeBrand);

      const matchRows = buildMatchingSheet(records, brand);
      if (matchRows.length > 1) {
        const wsMatch = XLSX.utils.aoa_to_sheet(matchRows);
        autoColWidths(wsMatch);
        const abbr = BRAND_PREFIX_MAP[brand] || brand.slice(0, 3).toUpperCase();
        const matchName = `${abbr} Sublytics & Auth.net`;
        XLSX.utils.book_append_sheet(wb, wsMatch, matchName.slice(0, 31));
      }
    });

  const dateStr = dayjs().format("YYYY-MM-DD");
  XLSX.writeFile(wb, `Sales Report ${dateStr}.xlsx`);
};

// --- Daily Sales Dashboard Excel — matches example Sales-Dashboard-2026-02-26.xlsx ---
const PRIMARY_PLATFORMS = ["Sublytics", "Wordpress"];
const GATEWAY_SOURCES_LIST = ["AUTH.NET #1", "AUTH.NET #2", "NMI Payarc", "Stripe", "Reliable Solutions", "Elavon"];
const STATUS_ORDER = ["Settled", "Pending", "Declined", "Refunded", "Captured", "Processed", "unsettled"];

function buildDashboardSheetData(records, dateRange, clientLabel) {
  const allSummary = calculateSalesSummary(records);
  const reportDate = dayjs().format("MMM DD, YYYY HH:mm:ss");
  const periodFrom = dateRange?.[0]?.format("MMM DD, YYYY") || reportDate;
  const periodTo = dateRange?.[1]?.format("MMM DD, YYYY") || periodFrom;

  const orderRecords = records.filter(
    (r) => r.source === "Sublytics" || r.source === "Wordpress"
  );
  const primarySummary = calculateSalesSummary(orderRecords);
  const totalRevenue = primarySummary.totalRevenue || 0;

  const ttRecords = records.filter((r) => r.source === "TigerTech");
  const ttSummary = calculateSalesSummary(ttRecords);

  const nxRecords = records.filter((r) => r.source === "NexRep");
  const nxSummary = calculateSalesSummary(nxRecords);
  const nxMatched = nxRecords.filter((r) => r.matched_sublytics || r._matched_sub_order_id).length;
  const nxPct = nxRecords.length > 0 ? ((nxMatched / nxRecords.length) * 100).toFixed(1) + "%" : "0%";

  const rpmRecords = records.filter((r) => r.source === "RPM");
  const rpmSummary = calculateSalesSummary(rpmRecords);
  const rpmMatched = rpmRecords.filter((r) => r.matched_sublytics || r._matched_sub_order_id).length;
  const rpmPct = rpmRecords.length > 0 ? ((rpmMatched / rpmRecords.length) * 100).toFixed(1) + "%" : "0%";

  const statusLabels = STATUS_ORDER.filter((s) => primarySummary.byStatus[s]);
  Object.keys(primarySummary.byStatus || {}).forEach((s) => {
    if (!statusLabels.includes(s)) statusLabels.push(s);
  });
  const statusBreakdownWithPct = statusLabels.map((s) => {
    const d = primarySummary.byStatus[s] || { count: 0, revenue: 0 };
    const pct = primarySummary.totalOrders > 0 ? ((d.count / primarySummary.totalOrders) * 100).toFixed(1) + "%" : "0%";
    return [s, d.count, d.revenue, pct];
  });

  const sublyticsOnly = records.filter((r) => r.source === "Sublytics");
  const ccOnly = sublyticsOnly.filter((r) => !isOfflineMethod(r));
  const offlineOnly = sublyticsOnly.filter((r) => isOfflineMethod(r));
  const ttMatched = sublyticsOnly.filter((r) => r.matched_tigertech).length;
  const gwMatched = ccOnly.filter((r) => r.matched_gateway).length;
  const ttPct = sublyticsOnly.length > 0 ? ((ttMatched / sublyticsOnly.length) * 100).toFixed(1) + "%" : "0%";
  const gwPct = ccOnly.length > 0 ? ((gwMatched / ccOnly.length) * 100).toFixed(1) + "%" : "0%";

  const gwRecords = records.filter((r) =>
    ["AUTH.NET #1", "AUTH.NET #2", "NMI Payarc", "Stripe", "Reliable Solutions", "Elavon"].includes(r.source)
  );
  const gatewaySummary = calculateSalesSummary(gwRecords);

  return {
    clientLabel,
    reportDate,
    periodFrom,
    periodTo,
    summary: primarySummary,
    allSummary,
    ttSummary,
    nxSummary,
    nxMatched,
    nxPct,
    rpmSummary,
    rpmMatched,
    rpmPct,
    gatewaySummary,
    totalRevenue,
    statusBreakdownWithPct,
    orderRecords,
    sublyticsOnly,
    ccOnly,
    offlineOnly,
    ttMatched,
    gwMatched,
    ttPct,
    gwPct,
  };
}

const DARK_NAVY = "FF0F172A";
const MED_NAVY = "FF1E293B";
const TEAL_ACCENT = "FF2DD4BF";
const WHITE = "FFFFFFFF";
const LIGHT_SLATE = "FFF1F5F9";
const BORDER_SLATE = "FFE2E8F0";
const TEXT_DARK = "FF1E293B";
const TEXT_MED = "FF475569";
const YELLOW_BG = "FFFFF9C4";
const YELLOW_BORDER = "FFFDD835";
const GREEN_BG = "FFC8E6C9";
const GREEN_BORDER = "FF66BB6A";
const BLUE_BG = "FFDBEAFE";
const PURPLE_BG = "FFE9D5FF";
const RED_BG = "FFFEE2E2";
const ORANGE_BG = "FFFFEDD5";

const dashBorder = {
  top: { style: "thin", color: { argb: BORDER_SLATE } },
  bottom: { style: "thin", color: { argb: BORDER_SLATE } },
  left: { style: "thin", color: { argb: BORDER_SLATE } },
  right: { style: "thin", color: { argb: BORDER_SLATE } },
};

function styleTableHeader(ws, row, cols, bgColor = DARK_NAVY, fgColor = WHITE) {
  const r = ws.getRow(row);
  r.height = 26;
  cols.forEach((val, i) => {
    const c = r.getCell(i + 1);
    c.value = val;
    c.font = { bold: true, size: 10, color: { argb: fgColor } };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgColor } };
    c.alignment = { vertical: "middle", horizontal: i === 0 ? "left" : "center" };
    c.border = dashBorder;
  });
}

function styleSectionBanner(ws, row, text, colSpan = 6, bgColor = MED_NAVY) {
  ws.mergeCells(row, 1, row, colSpan);
  const c = ws.getCell(row, 1);
  c.value = text;
  c.font = { bold: true, size: 12, color: { argb: WHITE } };
  c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgColor } };
  c.alignment = { vertical: "middle" };
  c.border = dashBorder;
  ws.getRow(row).height = 28;
}

function styleDataRow(ws, row, values, isAlt = false, moneyIdxs = []) {
  const r = ws.getRow(row);
  values.forEach((val, i) => {
    const c = r.getCell(i + 1);
    c.value = val;
    c.font = { size: 10, color: { argb: TEXT_DARK } };
    c.alignment = { vertical: "middle", horizontal: i === 0 ? "left" : "center" };
    c.border = dashBorder;
    if (isAlt) c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: LIGHT_SLATE } };
    if (moneyIdxs.includes(i)) c.numFmt = '"$"#,##0.00';
  });
}

function styleTotalRow(ws, row, values, moneyIdxs = []) {
  const r = ws.getRow(row);
  r.height = 26;
  values.forEach((val, i) => {
    const c = r.getCell(i + 1);
    c.value = val;
    c.font = { bold: true, size: 10, color: { argb: DARK_NAVY } };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2E8F0" } };
    c.alignment = { vertical: "middle", horizontal: i === 0 ? "left" : "center" };
    c.border = dashBorder;
    if (moneyIdxs.includes(i)) c.numFmt = '"$"#,##0.00';
  });
}

async function addDashboardChartsSheet(wb, allRecords, dateRange, chartImages, mailOrderData) {
  const ws = wb.addWorksheet("Dashboard", { views: [{ state: "frozen", ySplit: 0 }] });

  ws.getColumn(1).width = 4;
  ws.getColumn(2).width = 20;
  ws.getColumn(3).width = 16;
  ws.getColumn(4).width = 16;
  ws.getColumn(5).width = 16;
  ws.getColumn(6).width = 16;
  ws.getColumn(7).width = 16;
  ws.getColumn(8).width = 16;
  ws.getColumn(9).width = 4;
  ws.getColumn(10).width = 20;
  ws.getColumn(11).width = 22;
  ws.getColumn(12).width = 20;
  ws.getColumn(13).width = 4;
  ws.getColumn(14).width = 20;
  ws.getColumn(15).width = 22;
  ws.getColumn(16).width = 20;
  ws.getColumn(17).width = 4;
  ws.getColumn(18).width = 20;
  ws.getColumn(19).width = 22;
  ws.getColumn(20).width = 20;
  ws.getColumn(21).width = 4;
  ws.getColumn(22).width = 20;
  ws.getColumn(23).width = 18;

  const bd = {
    top: { style: "thin", color: { argb: "FF999999" } },
    bottom: { style: "thin", color: { argb: "FF999999" } },
    left: { style: "thin", color: { argb: "FF999999" } },
    right: { style: "thin", color: { argb: "FF999999" } },
  };
  const darkFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2D2D2D" } };
  const headerFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4A4A4A" } };
  const lightGrey = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } };
  const totalFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0E0E0" } };

  const dateOfReport = dayjs().format("D/M/YYYY");
  const periodFrom = dateRange?.[0]?.format("D/M/YYYY") || dateOfReport;
  const periodTo = dateRange?.[1]?.format("D/M/YYYY") || periodFrom;

  const setCell = (r, c, val, opts = {}) => {
    const cell = ws.getCell(r, c);
    cell.value = val;
    cell.font = { size: 10, ...opts.font };
    if (opts.fill) cell.fill = opts.fill;
    if (opts.border) cell.border = opts.border;
    if (opts.alignment) cell.alignment = opts.alignment;
    if (opts.numFmt) cell.numFmt = opts.numFmt;
  };

  const normalizeBrandKey = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const normalizeWeeklyByBrand = (raw) => {
    const out = {};
    Object.entries(raw || {}).forEach(([brand, value]) => {
      const normalized = normalizeBrandKey(brand);
      if (!normalized) return;
      out[normalized] = (out[normalized] || 0) + Number(value || 0);
    });
    return out;
  };

  const normalizeMonthlyByBrandCanonical = (raw) => {
    const out = {};
    Object.entries(raw || {}).forEach(([brand, monthMap]) => {
      const normalized = normalizeBrandKey(brand);
      if (!normalized) return;
      out[normalized] = out[normalized] || {};
      Object.entries(monthMap || {}).forEach(([monthKey, value]) => {
        const monthNum = Number(monthKey);
        if (!Number.isFinite(monthNum)) return;
        out[normalized][monthNum] = (out[normalized][monthNum] || 0) + Number(value || 0);
      });
    });
    return out;
  };

  const resolveBrandBucketValue = (bucket, brandName) => {
    if (!bucket || !brandName) return undefined;
    const target = normalizeBrandKey(brandName);
    if (!target) return undefined;
    return bucket[target];
  };

  let row = 1;

  setCell(row, 1, "Date of the report:", { font: { bold: true, size: 10, underline: true } });
  setCell(row, 3, dateOfReport);
  row++;
  setCell(row, 1, "Transaction from:", { font: { bold: true, size: 10, underline: true } });
  setCell(row, 2, periodFrom);
  setCell(row, 3, "To:");
  setCell(row, 4, periodTo);
  row++;
  setCell(row, 1, "Responsible:", { font: { bold: true, size: 10, underline: true } });
  row += 2;

  const webSubOrders = allRecords.filter((r) => r.source === "Sublytics" || r.source === "Wordpress");
  const ttOrders = allRecords.filter((r) => r.source === "TigerTech");
  const nxOrders = allRecords.filter((r) => r.source === "NexRep");
  const rpmOrders = allRecords.filter((r) => r.source === "RPM");
  const clientBuckets = {};
  const ensureBucket = (c) => { if (!clientBuckets[c]) clientBuckets[c] = { ws: [], tt: [], nx: [], rpm: [] }; };
  webSubOrders.forEach((r) => { const c = r.client || "Unknown"; ensureBucket(c); clientBuckets[c].ws.push(r); });
  ttOrders.forEach((r) => { const c = r.client || "Unknown"; ensureBucket(c); clientBuckets[c].tt.push(r); });
  nxOrders.forEach((r) => { const c = r.client || "Unknown"; ensureBucket(c); clientBuckets[c].nx.push(r); });
  rpmOrders.forEach((r) => { const c = r.client || "Unknown"; ensureBucket(c); clientBuckets[c].rpm.push(r); });
  const clientNames = Object.keys(clientBuckets).sort();

  const applyMergedHeader = (r, startCol, endCol, title) => {
    ws.mergeCells(r, startCol, r, endCol);
    setCell(r, startCol, title, { font: { bold: true, size: 11, color: { argb: "FFFFFFFF" } }, fill: darkFill, border: bd, alignment: { horizontal: "center", vertical: "middle" } });
    for (let c = startCol + 1; c <= endCol; c++) ws.getCell(r, c).border = bd;
  };
  applyMergedHeader(row, 2, 8, "WEBSITE AND SUBLYTICS");
  applyMergedHeader(row, 10, 12, "TIGERTECH");
  applyMergedHeader(row, 14, 16, "NEXREP");
  applyMergedHeader(row, 18, 20, "RPM");
  ws.getRow(row).height = 26;
  row++;

  const wsColHdrs = [
    "Clients",
    "Sublytics Orders",
    "Sublytics Amount",
    "Website Orders",
    "Website Amount",
    "Total No of Orders",
    "Sales Revenue",
  ];
  const otherColHdrs = ["Clients", "Total No of Orders", "Sales Revenue"];
  wsColHdrs.forEach((h, i) => {
    setCell(row, 2 + i, h, { font: { bold: true, size: 10, color: { argb: "FFFFFFFF" } }, fill: headerFill, border: bd, alignment: { horizontal: i === 0 ? "left" : "center", vertical: "middle" } });
  });
  otherColHdrs.forEach((h, i) => {
    setCell(row, 10 + i, h, { font: { bold: true, size: 10, color: { argb: "FFFFFFFF" } }, fill: headerFill, border: bd, alignment: { horizontal: i === 0 ? "left" : "center", vertical: "middle" } });
    setCell(row, 14 + i, h, { font: { bold: true, size: 10, color: { argb: "FFFFFFFF" } }, fill: headerFill, border: bd, alignment: { horizontal: i === 0 ? "left" : "center", vertical: "middle" } });
    setCell(row, 18 + i, h, { font: { bold: true, size: 10, color: { argb: "FFFFFFFF" } }, fill: headerFill, border: bd, alignment: { horizontal: i === 0 ? "left" : "center", vertical: "middle" } });
  });
  ws.getRow(row).height = 22;
  row++;

  const dataStart = row;

  const sumAmt = (rows) => rows.reduce((s, r) => s + (r.amount || 0), 0);
  let rowIdx = 0;
  clientNames.forEach((client) => {
    const b = clientBuckets[client];
    const subRows = b.ws.filter((r) => r.source === "Sublytics");
    const wpRows = b.ws.filter((r) => r.source === "Wordpress");
    const ttCount = b.tt.length;
    const ttRev = sumAmt(b.tt);
    const nxCount = b.nx.length;
    const nxRev = sumAmt(b.nx);
    const rpmCount = b.rpm.length;
    const rpmRev = sumAmt(b.rpm);
    const rowFill = rowIdx % 2 === 1 ? lightGrey : undefined;
    setCell(row, 2, client, { border: bd, fill: rowFill });
    setCell(row, 3, subRows.length, { border: bd, alignment: { horizontal: "center" }, fill: rowFill });
    setCell(row, 4, sumAmt(subRows), { border: bd, numFmt: '"$"#,##0.00', alignment: { horizontal: "right" }, fill: rowFill });
    setCell(row, 5, wpRows.length, { border: bd, alignment: { horizontal: "center" }, fill: rowFill });
    setCell(row, 6, sumAmt(wpRows), { border: bd, numFmt: '"$"#,##0.00', alignment: { horizontal: "right" }, fill: rowFill });
    setCell(row, 7, b.ws.length, { border: bd, alignment: { horizontal: "center" }, fill: rowFill });
    setCell(row, 8, sumAmt(b.ws), { border: bd, numFmt: '"$"#,##0.00', alignment: { horizontal: "right" }, fill: rowFill });
    setCell(row, 10, client, { border: bd, fill: rowFill });
    setCell(row, 11, ttCount, { border: bd, alignment: { horizontal: "center" }, fill: rowFill });
    setCell(row, 12, ttRev, { border: bd, numFmt: '"$"#,##0.00', alignment: { horizontal: "right" }, fill: rowFill });
    setCell(row, 14, client, { border: bd, fill: rowFill });
    setCell(row, 15, nxCount, { border: bd, alignment: { horizontal: "center" }, fill: rowFill });
    setCell(row, 16, nxRev, { border: bd, numFmt: '"$"#,##0.00', alignment: { horizontal: "right" }, fill: rowFill });
    setCell(row, 18, client, { border: bd, fill: rowFill });
    setCell(row, 19, rpmCount, { border: bd, alignment: { horizontal: "center" }, fill: rowFill });
    setCell(row, 20, rpmRev, { border: bd, numFmt: '"$"#,##0.00', alignment: { horizontal: "right" }, fill: rowFill });
    row++;
    rowIdx++;
  });

  const dataEnd = row - 1;

  const applyTotal = (r, c, val, opts = {}) => {
    const cell = ws.getCell(r, c);
    cell.value = val;
    cell.font = { bold: true, size: 10 };
    cell.fill = totalFill;
    cell.border = bd;
    if (opts.numFmt) cell.numFmt = opts.numFmt;
    if (opts.alignment) cell.alignment = opts.alignment;
  };

  applyTotal(row, 2, "Total");
  applyTotal(row, 3, { formula: `SUM(C${dataStart}:C${dataEnd})` }, { alignment: { horizontal: "center" } });
  applyTotal(row, 4, { formula: `SUM(D${dataStart}:D${dataEnd})` }, { numFmt: '"$"#,##0.00', alignment: { horizontal: "right" } });
  applyTotal(row, 5, { formula: `SUM(E${dataStart}:E${dataEnd})` }, { alignment: { horizontal: "center" } });
  applyTotal(row, 6, { formula: `SUM(F${dataStart}:F${dataEnd})` }, { numFmt: '"$"#,##0.00', alignment: { horizontal: "right" } });
  applyTotal(row, 7, { formula: `SUM(G${dataStart}:G${dataEnd})` }, { alignment: { horizontal: "center" } });
  applyTotal(row, 8, { formula: `SUM(H${dataStart}:H${dataEnd})` }, { numFmt: '"$"#,##0.00', alignment: { horizontal: "right" } });
  applyTotal(row, 10, "Total");
  applyTotal(row, 11, { formula: `SUM(K${dataStart}:K${dataEnd})` }, { alignment: { horizontal: "center" } });
  applyTotal(row, 12, { formula: `SUM(L${dataStart}:L${dataEnd})` }, { numFmt: '"$"#,##0.00', alignment: { horizontal: "right" } });
  applyTotal(row, 14, "Total");
  applyTotal(row, 15, { formula: `SUM(O${dataStart}:O${dataEnd})` }, { alignment: { horizontal: "center" } });
  applyTotal(row, 16, { formula: `SUM(P${dataStart}:P${dataEnd})` }, { numFmt: '"$"#,##0.00', alignment: { horizontal: "right" } });
  applyTotal(row, 18, "Total");
  applyTotal(row, 19, { formula: `SUM(S${dataStart}:S${dataEnd})` }, { alignment: { horizontal: "center" } });
  applyTotal(row, 20, { formula: `SUM(T${dataStart}:T${dataEnd})` }, { numFmt: '"$"#,##0.00', alignment: { horizontal: "right" } });
  ws.getRow(row).height = 24;

  const endDate = dateRange?.[1] || dayjs();
  const mondayOfWeek = endDate.isoWeekday(1);
  const sundayOfWeek = endDate.isoWeekday(7);
  const mondayStr = mondayOfWeek.format("D/M/YYYY");
  const sundayStr = sundayOfWeek.format("D/M/YYYY");

  const parseRecDate = (r) => {
    const d = r.date || r.raw_date || "";
    return d ? dayjs(d.slice(0, 10)) : null;
  };

  // One daily map: stored Sublytics cash, then overwrite any day in this export
  // with the same Cash rows / amounts as Individual Sales (includes shipping).
  const dailyCash = {};
  try {
    const resp = await fetchCashControlData(endDate.format("YYYY-MM-DD"));
    Object.entries(resp?.data?.daily?.by_date || {}).forEach(([dayKey, brandMap]) => {
      dailyCash[dayKey] = normalizeWeeklyByBrand(brandMap);
    });
  } catch (e) {
    console.warn("[export] Could not fetch stored daily cash, using live Sublytics cash only:", e);
  }

  const liveByDay = {};
  allRecords.forEach((r) => {
    if (r.source !== "Cash") return;
    const rd = parseRecDate(r);
    if (!rd || !rd.isValid()) return;
    const dayKey = rd.format("YYYY-MM-DD");
    const normalized = normalizeBrandKey(r.client);
    if (!normalized) return;
    if (!liveByDay[dayKey]) liveByDay[dayKey] = {};
    liveByDay[dayKey][normalized] = (liveByDay[dayKey][normalized] || 0) + (Number(r.amount) || 0);
  });
  Object.entries(liveByDay).forEach(([dayKey, brandMap]) => {
    dailyCash[dayKey] = brandMap;
  });

  const weeklyCash = {};
  const storedByBrand = {};
  Object.entries(dailyCash).forEach(([dayKey, brandMap]) => {
    const day = dayjs(dayKey);
    if (!day.isValid()) return;
    const monthNum = day.month() + 1;
    const inWeek = !day.isBefore(mondayOfWeek, "day") && !day.isAfter(sundayOfWeek, "day");
    Object.entries(brandMap || {}).forEach(([normalized, value]) => {
      const amt = Number(value) || 0;
      if (inWeek) weeklyCash[normalized] = (weeklyCash[normalized] || 0) + amt;
      if (!storedByBrand[normalized]) storedByBrand[normalized] = {};
      storedByBrand[normalized][monthNum] = (storedByBrand[normalized][monthNum] || 0) + amt;
    });
  });

  if (!Object.keys(storedByBrand).length) {
    try {
      const resp = await fetchMonthlyCashTotals(endDate.year());
      Object.assign(storedByBrand, normalizeMonthlyByBrandCanonical(resp?.data?.by_brand || {}));
    } catch (e2) {
      console.warn("[export] Could not fetch monthly cash totals either:", e2);
    }
  }

  const yellowFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFDD835" } };
  const choosePreferredLabel = (current, candidate) => {
    if (!current) return candidate;
    if (!candidate) return current;
    const currentHasSpace = /\s/.test(current);
    const candidateHasSpace = /\s/.test(candidate);
    if (candidateHasSpace && !currentHasSpace) return candidate;
    const currentAllCaps = current === current.toUpperCase();
    const candidateAllCaps = candidate === candidate.toUpperCase();
    if (!candidateAllCaps && currentAllCaps) return candidate;
    return current;
  };

  const canonicalCashLabelMap = {};
  const registerCashLabel = (label) => {
    const normalized = normalizeBrandKey(label);
    if (!normalized) return;
    canonicalCashLabelMap[normalized] = choosePreferredLabel(canonicalCashLabelMap[normalized], label);
  };
  const hasNonZeroMonthlyValue = (monthMap) =>
    Object.values(monthMap || {}).some((v) => Number(v || 0) !== 0);
  clientNames.forEach(registerCashLabel);
  Object.entries(weeklyCash || {}).forEach(([normalized, value]) => {
    // Keep weekly-only brands only when they actually carry weekly cash.
    if (Number(value || 0) === 0) return;
    if (!canonicalCashLabelMap[normalized]) canonicalCashLabelMap[normalized] = normalized;
  });
  Object.entries(storedByBrand || {}).forEach(([normalized, monthMap]) => {
    // Ignore synthetic uploaded labels that are all zeros across months.
    if (!hasNonZeroMonthlyValue(monthMap)) return;
    if (!canonicalCashLabelMap[normalized]) canonicalCashLabelMap[normalized] = normalized;
  });

  const cashClientNames = Object.keys(canonicalCashLabelMap).map((normalized) => ({
    normalized,
    label: canonicalCashLabelMap[normalized],
  })).sort((a, b) => a.label.localeCompare(b.label));

  // ── Weekly Cash Control (columns V-W; RPM occupies R-T after WS split) ──
  const ccCol = 22;
  const ccAmtCol = 23;
  const ccHeaderRow = dataStart - 3;
  ws.mergeCells(ccHeaderRow, ccCol, ccHeaderRow, ccAmtCol);
  setCell(ccHeaderRow, ccCol, "CASH CONTROL", {
    font: { bold: true, size: 11, color: { argb: "FF000000" } },
    fill: yellowFill, border: bd,
    alignment: { horizontal: "center", vertical: "middle" },
  });
  ws.getCell(ccHeaderRow, ccAmtCol).border = bd;

  setCell(ccHeaderRow + 1, ccCol, "Transaction From", { font: { bold: true, size: 9, color: { argb: "FF475569" } } });
  setCell(ccHeaderRow + 1, ccAmtCol, `${mondayStr}  To  ${sundayStr}`, { font: { size: 9 } });

  const ccHdrRow = dataStart - 1;
  setCell(ccHdrRow, ccCol, "Clients", {
    font: { bold: true, size: 10, color: { argb: "FFFFFFFF" } },
    fill: headerFill, border: bd,
    alignment: { horizontal: "left", vertical: "middle" },
  });
  setCell(ccHdrRow, ccAmtCol, "Amount of Cash", {
    font: { bold: true, size: 10, color: { argb: "FFFFFFFF" } },
    fill: headerFill, border: bd,
    alignment: { horizontal: "center", vertical: "middle" },
  });

  let weeklyTotal = 0;
  cashClientNames.forEach((client, idx) => {
    const r = dataStart + idx;
    const amt = Number(resolveBrandBucketValue(weeklyCash, client.normalized) || 0);
    weeklyTotal += amt;
    const rowFill = idx % 2 === 1 ? lightGrey : undefined;
    setCell(r, ccCol, client.label, { border: bd, fill: rowFill });
    setCell(r, ccAmtCol, amt, { border: bd, numFmt: '"$"#,##0.00', alignment: { horizontal: "right" }, fill: rowFill });
  });

  const weeklyDataEnd = dataStart + Math.max(cashClientNames.length - 1, 0);
  const weeklyTotalRow = weeklyDataEnd + 1;
  applyTotal(weeklyTotalRow, ccCol, "Total");
  applyTotal(weeklyTotalRow, ccAmtCol, weeklyTotal, { numFmt: '"$"#,##0.00', alignment: { horizontal: "right" } });

  // ── Monthly Cash Control (below weekly, columns V outward) ──
  const currentYear = endDate.year();
  const currentMonth = endDate.month() + 1;
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const mcColStart = 22;
  const mcTotalCol = mcColStart + 1 + cashClientNames.length;

  for (let ci = mcColStart; ci <= mcTotalCol; ci++) {
    const curW = ws.getColumn(ci).width || 10;
    ws.getColumn(ci).width = Math.max(curW, 14);
  }

  let mcRow = weeklyTotalRow + 2;
  ws.mergeCells(mcRow, mcColStart, mcRow, mcTotalCol);
  setCell(mcRow, mcColStart, "MONTHLY CASH CONTROL", {
    font: { bold: true, size: 11, color: { argb: "FF000000" } },
    fill: yellowFill, border: bd,
    alignment: { horizontal: "center", vertical: "middle" },
  });
  mcRow++;

  setCell(mcRow, mcColStart, "Months", {
    font: { bold: true, size: 10, color: { argb: "FFFFFFFF" } },
    fill: headerFill, border: bd,
    alignment: { horizontal: "center", vertical: "middle" },
  });
  cashClientNames.forEach((brand, ci) => {
    setCell(mcRow, mcColStart + 1 + ci, brand.label, {
      font: { bold: true, size: 10, color: { argb: "FFFFFFFF" } },
      fill: headerFill, border: bd,
      alignment: { horizontal: "center", vertical: "middle" },
    });
  });
  setCell(mcRow, mcTotalCol, "Total", {
    font: { bold: true, size: 10, color: { argb: "FFFFFFFF" } },
    fill: headerFill, border: bd,
    alignment: { horizontal: "center", vertical: "middle" },
  });
  mcRow++;

  const yearlyBrandTotals = {};
  cashClientNames.forEach((b) => { yearlyBrandTotals[b.normalized] = 0; });

  for (let mIdx = 0; mIdx < 12; mIdx++) {
    const mNum = mIdx + 1;
    const rowFill = mIdx % 2 === 1 ? lightGrey : undefined;
    setCell(mcRow, mcColStart, monthNames[mIdx], {
      font: { bold: true, size: 10 }, fill: rowFill, border: bd,
      alignment: { horizontal: "center" },
    });
    let rowTotal = 0;
    cashClientNames.forEach((brand, ci) => {
      const monthlyEntry = resolveBrandBucketValue(storedByBrand, brand.normalized) || {};
      const val = mNum <= currentMonth ? Number(monthlyEntry[mNum] || monthlyEntry[String(mNum)] || 0) : 0;
      yearlyBrandTotals[brand.normalized] += val;
      rowTotal += val;
      if (val) {
        setCell(mcRow, mcColStart + 1 + ci, val, {
          fill: rowFill, border: bd, numFmt: '"$"#,##0.00',
          alignment: { horizontal: "right" },
        });
      } else {
        setCell(mcRow, mcColStart + 1 + ci, "-", {
          fill: rowFill, border: bd, alignment: { horizontal: "center" },
        });
      }
    });
    if (rowTotal) {
      setCell(mcRow, mcTotalCol, rowTotal, {
        fill: rowFill, border: bd, numFmt: '"$"#,##0.00',
        alignment: { horizontal: "right" },
      });
    } else {
      setCell(mcRow, mcTotalCol, "-", {
        fill: rowFill, border: bd, alignment: { horizontal: "center" },
      });
    }
    mcRow++;
  }

  let grandYearly = 0;
  setCell(mcRow, mcColStart, "Yearly Total", {
    font: { bold: true, size: 10 }, fill: totalFill, border: bd,
    alignment: { horizontal: "center" },
  });
  cashClientNames.forEach((brand, ci) => {
    const val = yearlyBrandTotals[brand.normalized];
    grandYearly += val;
    if (val) {
      applyTotal(mcRow, mcColStart + 1 + ci, val, { numFmt: '"$"#,##0.00', alignment: { horizontal: "right" } });
    } else {
      applyTotal(mcRow, mcColStart + 1 + ci, "-", { alignment: { horizontal: "center" } });
    }
  });
  applyTotal(mcRow, mcTotalCol, grandYearly, { numFmt: '"$"#,##0.00', alignment: { horizontal: "right" } });

  row += 3;

  const wsRevMap = {};
  const ttRevMap = {};
  clientNames.forEach((c) => {
    const b = clientBuckets[c];
    const wR = b.ws.reduce((s, r) => s + (r.amount || 0), 0);
    const tR = b.tt.reduce((s, r) => s + (r.amount || 0), 0);
    if (wR > 0) wsRevMap[c] = wR;
    if (tR > 0) ttRevMap[c] = tR;
  });

  const [wsPie, ttPie] = await Promise.all([
    piePngFromRevenueMap(wsRevMap, "Sales Revenue"),
    piePngFromRevenueMap(ttRevMap, "Sales Revenue"),
  ]);

  const embedPie = (dataUrl, col) => {
    if (!dataUrl) return;
    const m = dataUrl.match(/^data:image\/(png|jpeg);base64,(.+)$/i);
    const b64 = m ? m[2] : dataUrl.replace(/^data:image\/\w+;base64,/, "");
    const imgId = wb.addImage({ base64: b64, extension: "png" });
    ws.addImage(imgId, { tl: { col, row: row - 1 }, ext: { width: 420, height: 280 } });
  };

  embedPie(wsPie, 1);
  embedPie(ttPie, 5);

  const PIE_ROWS = 16;
  row += PIE_ROWS + 2;

  if (chartImages?.length) {
    const COLS_PER_ROW = 3;
    const IMG_W = 320;
    const IMG_H = 220;
    const COL_SPAN = 3.5;
    const CHART_ROWS = 14;
    const valid = chartImages.filter(({ dataUrl }) => dataUrl && typeof dataUrl === "string");

    for (let i = 0; i < valid.length; i++) {
      const colIdx = i % COLS_PER_ROW;
      if (colIdx === 0 && i > 0) row += CHART_ROWS + 2;
      if (colIdx === 0) {
        const titlesInRow = valid.slice(i, i + COLS_PER_ROW);
        titlesInRow.forEach((item, j) => {
          const c = Math.round(j * COL_SPAN);
          ws.getCell(row, c + 1).value = item.title || "";
          ws.getCell(row, c + 1).font = { bold: true, size: 10, color: { argb: "FF333333" } };
        });
        row += 1;
      }
      try {
        const { dataUrl } = valid[i];
        const m = dataUrl.match(/^data:image\/(png|jpeg|jpg);base64,(.+)$/i);
        const ext = m && /^jpe?g$/i.test(m[1]) ? "jpeg" : "png";
        const b64 = m ? m[2] : dataUrl.replace(/^data:image\/\w+;base64,/, "");
        const imgId = wb.addImage({ base64: b64, extension: ext });
        const colOffset = colIdx * COL_SPAN;
        ws.addImage(imgId, { tl: { col: colOffset, row: row - 1 }, ext: { width: IMG_W, height: IMG_H } });
      } catch (e) {
        console.warn("[export] skipped chart:", valid[i].title, e);
      }
    }
    row += CHART_ROWS + 2;
  }

  // ── Checks & Money Orders — Daily Payment Reconciliation (below charts) ──
  const checkByBrand = {};
  allRecords.forEach((r) => {
    if (r.source === "Check") {
      const brand = r.client || "Unknown";
      checkByBrand[brand] = (checkByBrand[brand] || 0) + (r.amount || 0);
    }
  });

  const moByBrand = {};
  const moTotalAmountByBrand = {};
  const moCheckValueByBrand = {};
  const moDepositAmountByBrand = {};
  const moSentToDirectorByBrand = {};
  const _moRawKeys = new Set();
  (mailOrderData || []).forEach((mo) => {
    const key = (mo.brand || "").trim().toUpperCase();
    _moRawKeys.add(key);
    moByBrand[key] = (moByBrand[key] || 0) + (parseFloat(mo.money_orders_value) || 0);
    moTotalAmountByBrand[key] = (moTotalAmountByBrand[key] || 0) + (parseFloat(mo.total_amount) || 0);
    moCheckValueByBrand[key] = (moCheckValueByBrand[key] || 0) + (parseFloat(mo.check_orders_value) || 0);
    moDepositAmountByBrand[key] = (moDepositAmountByBrand[key] || 0) + (parseFloat(mo.deposit_amount) || 0);
    moSentToDirectorByBrand[key] = (moSentToDirectorByBrand[key] || 0) + (parseFloat(mo.money_orders_sent_to_director) || 0);
  });
  const _resolveMoBrand = (clientName) => {
    const key = clientName.trim().toUpperCase();
    if (_moRawKeys.has(key)) return key;
    for (const mk of _moRawKeys) {
      if (key.includes(mk) || mk.includes(key)) return mk;
    }
    return key;
  };

  for (let ci = 1; ci <= 16; ci++) {
    const curW = ws.getColumn(ci).width || 10;
    ws.getColumn(ci).width = Math.max(curW, 15);
  }
  ws.getColumn(7).width = 4;
  ws.getColumn(13).width = 4;

  ws.mergeCells(row, 1, row, 16);
  setCell(row, 1, "CHECKS AND MONEY ORDERS-DAILY PAYMENT RECONCILIATION", {
    font: { bold: true, size: 11, color: { argb: "FFFFFFFF" } },
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E3A5F" } },
    border: bd,
    alignment: { horizontal: "center", vertical: "middle" },
  });
  ws.getRow(row).height = 26;
  row++;

  {
    const rw = ws.getRow(row);
    rw.height = 22;
    const setMerged = (s, e, text, bg) => {
      ws.mergeCells(row, s, row, e);
      const c = rw.getCell(s);
      c.value = text;
      c.font = { bold: true, size: 10, color: { argb: "FFFFFFFF" } };
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
      c.alignment = { horizontal: "center", vertical: "middle" };
      c.border = bd;
      for (let ci = s + 1; ci <= e; ci++) {
        const sc = rw.getCell(ci);
        sc.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
        sc.border = bd;
      }
    };
    setMerged(2, 6, "CHECKS", "FFC62828");
    setMerged(8, 12, "MONEY ORDERS", "FF1565C0");
    setMerged(14, 16, "UNDERPAYMENT ANALYSIS", "FF2E7D32");
    row++;
  }

  const navyFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E3A5F" } };
  const reconHdrs = [
    "Client",
    "CRM\n(Sublytics) $", "Bank\nAccount $", "Difference $", "Comment", "Running Balance",
    "",
    "CRM\n(Sublytics) $", "Bank\nAccount $", "Difference $", "Comment", "Running Balance for\nMoney orders $",
    "",
    "Total CRM $", "Actual\nReceived $", "Underpayment\nGap $",
  ];
  reconHdrs.forEach((h, i) => {
    const c = ws.getCell(row, i + 1);
    c.value = h;
    c.font = { bold: true, size: 9, color: { argb: "FFFFFFFF" } };
    c.fill = navyFill;
    c.border = bd;
    c.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  });
  ws.getRow(row).height = 30;
  row++;

  let checkTotal = 0;
  let moTotal = 0;
  let depositTotal = 0;
  let moBankTotal = 0;
  let checksRunningBalance = 0;
  let moneyOrdersRunningBalance = 0;
  const moneyIdxSet = new Set([2, 3, 4, 6, 8, 9, 10, 12, 14, 15, 16]);
  clientNames.forEach((brand, i) => {
    const brandKey = _resolveMoBrand(brand);
    const sublyticsCheck = checkByBrand[brand] || 0;
    const mo = moByBrand[brandKey] || 0;
    const trackingCheckVal = moCheckValueByBrand[brandKey] || 0;
    const depositAmt = moDepositAmountByBrand[brandKey] || 0;
    const moBankAmt = moSentToDirectorByBrand[brandKey] || 0;
    const checksCrm = sublyticsCheck > 0 ? sublyticsCheck - mo : trackingCheckVal;
    const moCrm = mo;
    checkTotal += checksCrm;
    moTotal += moCrm;
    depositTotal += depositAmt;
    moBankTotal += moBankAmt;
    const totalCrm = sublyticsCheck > 0 ? sublyticsCheck : checksCrm + moCrm;
    const actualReceived = moTotalAmountByBrand[brandKey] || 0;
    const gap = totalCrm - actualReceived;
    const rowFill = i % 2 === 1 ? lightGrey : undefined;
    const checksDiff = checksCrm - depositAmt;
    const moDiff = moCrm - moBankAmt;
    checksRunningBalance += checksDiff;
    moneyOrdersRunningBalance += moDiff;
    const vals = [brand, checksCrm, depositAmt, checksDiff, "", checksRunningBalance, "",
                  moCrm, moBankAmt, moDiff, "", moneyOrdersRunningBalance, "",
                  totalCrm, actualReceived, gap];
    vals.forEach((v, ci) => {
      const col = ci + 1;
      const c = ws.getCell(row, col);
      c.value = v;
      c.font = { size: 10 };
      if (rowFill) c.fill = rowFill;
      c.border = bd;
      if (moneyIdxSet.has(col)) c.numFmt = '"$"#,##0.00';
      c.alignment = { horizontal: col === 1 ? "left" : "center" };
    });
    row++;
  });

  const grandCrm = checkTotal + moTotal;
  const grandReceived = clientNames.reduce((sum, b) => sum + (moTotalAmountByBrand[_resolveMoBrand(b)] || 0), 0);
  const grandGap = grandCrm - grandReceived;
  const totVals = ["TOTAL", checkTotal, depositTotal, checkTotal - depositTotal, "", checksRunningBalance, "",
                   moTotal, moBankTotal, moTotal - moBankTotal, "", moneyOrdersRunningBalance, "",
                   grandCrm, grandReceived, grandGap];
  totVals.forEach((v, ci) => {
    const col = ci + 1;
    const c = ws.getCell(row, col);
    c.value = v;
    c.font = { bold: true, size: 10 };
    c.fill = totalFill;
    c.border = bd;
    if (moneyIdxSet.has(col)) c.numFmt = '"$"#,##0.00';
    c.alignment = { horizontal: col === 1 ? "left" : "center" };
  });
  ws.getRow(row).height = 24;
}

/**
 * Export Daily Sales Dashboard to Excel matching example structure.
 * Uses the provided records (should be filteredRecords so Filter by Source is respected).
 * If gridApi is provided (e.g. when "All Sources" tab is active), INDIVIDUAL SALES DATA
 * order and column filters match the grid.
 */
export const exportDailySalesDashboardExcel = async (
  allRecords,
  dateRange,
  brandNameMap,
  gridApi = null,
  chartImages = null,
  mailOrderData = null
) => {
  if (!allRecords || allRecords.length === 0) return;

  const wb = new ExcelJS.Workbook();
  await addDashboardChartsSheet(wb, allRecords, dateRange, chartImages, mailOrderData);

  const clientMap = groupByClient(allRecords);
  const clientNames = Object.keys(clientMap).sort();

  const gridOrderedRecords = gridApi ? getFilteredRowsFromGrid(gridApi, allRecords) : null;

  const sheetsToBuild = [
    { label: "All Clients", records: allRecords },
    ...clientNames.map((name) => ({ label: name, records: clientMap[name] })),
  ];

  for (let s = 0; s < sheetsToBuild.length; s++) {
    const { label, records } = sheetsToBuild[s];
    const data = buildDashboardSheetData(records, dateRange, label === "All Clients" ? "All Clients" : label);
    const sheetName = label.slice(0, 31);
    const ws = wb.addWorksheet(sheetName, { views: [{ state: "frozen", ySplit: 0 }] });

    ws.getColumn(1).width = 18;  // Source
    ws.getColumn(2).width = 18;  // Brand
    ws.getColumn(3).width = 18;  // Order ID
    ws.getColumn(4).width = 18;  // Transaction ID
    ws.getColumn(5).width = 22;  // Customer
    ws.getColumn(6).width = 26;  // Email
    ws.getColumn(7).width = 14;  // Amount ($)
    ws.getColumn(8).width = 16;  // Payment Type
    ws.getColumn(9).width = 14;  // Status
    ws.getColumn(10).width = 18; // Date
    ws.getColumn(11).width = 16; // Sublytics Match
    ws.getColumn(12).width = 16; // TigerTech Match
    ws.getColumn(13).width = 16; // Gateway Match
    ws.getColumn(14).width = 22; // AUTH.NET Txn ID
    ws.getColumn(15).width = 22; // NMI Txn ID
    ws.getColumn(16).width = 22; // Stripe Txn ID
    ws.getColumn(17).width = 22; // Elavon Txn ID
    ws.getColumn(18).width = 20; // Matched Via
    ws.getColumn(19).width = 16; // Reconciliation

    let row = 1;

    styleSectionBanner(ws, row, `  Daily Sales Report — ${data.clientLabel}`, 17, DARK_NAVY);
    row += 1;

    const metaItems = [
      ["Client / Brand", data.clientLabel],
      ["Report Generated", data.reportDate],
      ["Period", `${data.periodFrom}  →  ${data.periodTo}`],
    ];
    metaItems.forEach(([lbl, val]) => {
      ws.getCell(row, 1).value = lbl;
      ws.getCell(row, 1).font = { bold: true, size: 10, color: { argb: TEXT_MED } };
      ws.getCell(row, 2).value = val;
      ws.getCell(row, 2).font = { size: 10, color: { argb: TEXT_DARK } };
      row += 1;
    });
    row += 1;

    styleSectionBanner(ws, row, "  Summary", 3, "FF2563EB");
    row += 1;
    const summaryItems = [
      ["Total Orders", data.summary.totalOrders, false],
      ["Total Revenue", data.summary.totalRevenue, true],
      ["Pending", (data.summary.byStatus["Pending"] || {}).count || 0, false],
      ["Declined", (data.summary.byStatus["Declined"] || {}).count || 0, false],
      ["Refunded", (data.summary.byStatus["Refunded"] || {}).count || 0, false],
    ];
    summaryItems.forEach(([lbl, val, isMoney], i) => {
      styleDataRow(ws, row, [lbl, val, ""], i % 2 === 1, isMoney ? [1] : []);
      row += 1;
    });
    row += 1;

    styleSectionBanner(ws, row, "  Order Sources (Canonical)", 3, "FF059669");
    row += 1;
    styleTableHeader(ws, row, ["Platform", "Orders", "Revenue ($)"], "FF047857");
    row += 1;
    const platStart = row;
    const platEntries = PRIMARY_PLATFORMS.filter((p) => data.summary.bySource[p]);
    platEntries.forEach((p, i) => {
      const d = data.summary.bySource[p];
      styleDataRow(ws, row, [p, d.count, d.revenue], i % 2 === 1, [2]);
      row += 1;
    });
    const platEnd = row - 1;
    styleTotalRow(ws, row, ["Total", { formula: `SUM(B${platStart}:B${platEnd})` }, { formula: `SUM(C${platStart}:C${platEnd})` }], [2]);
    row += 2;

    if (data.ttSummary.totalOrders > 0) {
      styleSectionBanner(ws, row, "  TigerTech Verification", 3, "FF0891B2");
      row += 1;
      styleTableHeader(ws, row, ["Source", "Orders", "Revenue ($)"], "FF0E7490");
      row += 1;
      styleDataRow(ws, row, ["TigerTech", data.ttSummary.totalOrders, data.ttSummary.totalRevenue], false, [2]);
      row += 1;
      styleDataRow(ws, row, ["Sublytics (source of truth)", data.sublyticsOnly.length, data.sublyticsOnly.reduce((s, r) => s + (r.amount || 0), 0)], true, [2]);
      row += 1;
      const orderDiff = data.ttSummary.totalOrders - data.sublyticsOnly.length;
      const revDiff = data.ttSummary.totalRevenue - data.sublyticsOnly.reduce((s, r) => s + (r.amount || 0), 0);
      styleDataRow(ws, row, [`Difference (${orderDiff >= 0 ? "+" : ""}${orderDiff})`, orderDiff, revDiff], false, [2]);
      row += 2;
    }

    if (data.nxSummary.totalOrders > 0) {
      styleSectionBanner(ws, row, "  NexRep Verification", 3, "FF531DAB");
      row += 1;
      styleTableHeader(ws, row, ["Source", "Orders", "Revenue ($)"], "FF391085");
      row += 1;
      styleDataRow(ws, row, ["NexRep", data.nxSummary.totalOrders, data.nxSummary.totalRevenue], false, [2]);
      row += 1;
      styleDataRow(ws, row, ["Matched to Sublytics", data.nxMatched, data.nxPct], true, []);
      row += 2;
    }

    if (data.rpmSummary.totalOrders > 0) {
      styleSectionBanner(ws, row, "  RPM Verification", 3, "FF08979C");
      row += 1;
      styleTableHeader(ws, row, ["Source", "Orders", "Revenue ($)"], "FF006D75");
      row += 1;
      styleDataRow(ws, row, ["RPM", data.rpmSummary.totalOrders, data.rpmSummary.totalRevenue], false, [2]);
      row += 1;
      styleDataRow(ws, row, ["Matched to Sublytics", data.rpmMatched, data.rpmPct], true, []);
      row += 2;
    }

    styleSectionBanner(ws, row, "  Status Breakdown", 4, "FF0891B2");
    row += 1;
    styleTableHeader(ws, row, ["Status", "Orders", "Revenue ($)", "% of Total"], "FF0E7490");
    row += 1;
    const statusStart = row;
    data.statusBreakdownWithPct.forEach(([status, count, revenue, pct], i) => {
      styleDataRow(ws, row, [status, count, revenue, pct], i % 2 === 1, [2]);
      row += 1;
    });
    const statusEnd = row - 1;
    styleTotalRow(ws, row, ["Total", { formula: `SUM(B${statusStart}:B${statusEnd})` }, { formula: `SUM(C${statusStart}:C${statusEnd})` }, "100%"], [2]);
    row += 2;

    styleSectionBanner(ws, row, "  Sales Funnel", 3, "FFD97706");
    row += 1;
    styleTableHeader(ws, row, ["Stage", "Count", ""], "FFB45309");
    row += 1;
    const funnelStages = [
      ["Total Orders (Sublytics + WP)", data.orderRecords.length],
      ["Captured / Processed", data.orderRecords.filter((r) => ["Settled", "Captured", "Processed", "Pending"].includes(r.status)).length],
      ["Settled", data.orderRecords.filter((r) => ["Settled", "Captured", "Processed"].includes(r.status)).length],
      ["Pending", data.orderRecords.filter((r) => r.status === "Pending").length],
      ["Declined", data.orderRecords.filter((r) => r.status === "Declined").length],
      ["Refunded", data.orderRecords.filter((r) => r.status === "Refunded").length],
      [`TigerTech Matched (of ${data.sublyticsOnly.length})`, data.ttMatched],
      [`NexRep rows matched to Sublytics`, data.nxMatched],
      [`RPM rows matched to Sublytics`, data.rpmMatched],
    ];
    funnelStages.forEach(([stage, count], i) => {
      styleDataRow(ws, row, [stage, count, ""], i % 2 === 1);
      row += 1;
    });
    row += 1;

    const gwEntries = GATEWAY_SOURCES_LIST
      .filter((g) => data.allSummary.bySource[g])
      .map((g) => [g, data.allSummary.bySource[g]]);
    if (gwEntries.length > 0) {
      styleSectionBanner(ws, row, "  Payment Gateways", 3, "FF7C3AED");
      row += 1;
      styleTableHeader(ws, row, ["Gateway", "Transactions", "Amount ($)"], "FF6D28D9");
      row += 1;
      const gwStart = row;
      gwEntries.forEach(([src, d], i) => {
        styleDataRow(ws, row, [src, d.count, d.revenue], i % 2 === 1, [2]);
        row += 1;
      });
      const gwEnd = row - 1;
      styleTotalRow(ws, row, ["Total", { formula: `SUM(B${gwStart}:B${gwEnd})` }, { formula: `SUM(C${gwStart}:C${gwEnd})` }], [2]);
      row += 2;
    }

    styleSectionBanner(ws, row, "  Cross-Source Matching", 4, "FFBE185D");
    row += 1;
    styleTableHeader(ws, row, ["Metric", "Count", "%", ""], "FF9F1239");
    row += 1;
    styleDataRow(ws, row, ["Total Sublytics Orders", data.sublyticsOnly.length, "", ""], false);
    row += 1;
    styleDataRow(ws, row, ["  Credit Card Orders", data.ccOnly.length, "", ""], true);
    row += 1;
    styleDataRow(ws, row, ["  Offline (Check/Cash/MO)", data.offlineOnly.length, "", ""], false);
    row += 1;
    styleDataRow(ws, row, ["TigerTech Matched", data.ttMatched, data.ttPct, ""], true);
    row += 1;
    styleDataRow(ws, row, ["NexRep Matched to Sublytics", data.nxMatched, data.nxPct, ""], false);
    row += 1;
    styleDataRow(ws, row, ["RPM Matched to Sublytics", data.rpmMatched, data.rpmPct, ""], true);
    row += 1;
    styleDataRow(ws, row, ["Gateway Matched (CC only)", data.gwMatched, data.gwPct, ""], false);
    row += 1;
    const discrepancy = data.ccOnly.length - data.gwMatched;
    const discPct = data.ccOnly.length > 0 ? ((discrepancy / data.ccOnly.length) * 100).toFixed(1) + "%" : "0%";
    styleDataRow(ws, row, ["Missing in Gateway", discrepancy, discPct, ""], true);
    row += 2;

    {
      const ttRecords = records.filter((r) => r.source === "TigerTech");
      if (ttRecords.length > 0 || data.ttMatched > 0) {
        styleSectionBanner(ws, row, "  TigerTech Matching Detail", 4, "FF0891B2");
        row += 1;
        styleTableHeader(ws, row, ["Match Criteria", "Count", "%", ""], "FF0E7490");
        row += 1;

        const ttByEmail = new Map();
        const ttByTxn = new Map();
        const ttByOrder = new Map();
        for (const t of ttRecords) {
          const em = (t.customer_email || "").trim().toLowerCase();
          const mid = (t.transaction_id || "").trim().toLowerCase();
          const oid = (t.order_id || "").trim().toLowerCase();
          if (em && !em.includes("noemail")) ttByEmail.set(em, t);
          if (mid) ttByTxn.set(mid, t);
          if (oid) ttByOrder.set(oid, t);
        }
        let matchByOid = 0, matchByTxn = 0, matchByEmail = 0, matchOther = 0;
        for (const s of data.sublyticsOnly) {
          if (!s.matched_tigertech) continue;
          const sOid = (s.order_id || "").trim().toLowerCase();
          const sTxn = (s.transaction_id || "").trim().toLowerCase();
          const sEm = (s.customer_email || "").trim().toLowerCase();
          if (sOid && ttByOrder.has(sOid)) matchByOid++;
          else if (sTxn && ttByTxn.has(sTxn)) matchByTxn++;
          else if (sEm && !sEm.includes("noemail") && ttByEmail.has(sEm)) matchByEmail++;
          else matchOther++;
        }
        const ttTotal = data.ttMatched || 1;
        const pctOf = (n) => ((n / ttTotal) * 100).toFixed(1) + "%";
        styleDataRow(ws, row, ["By Order ID", matchByOid, pctOf(matchByOid), ""], false);
        row += 1;
        styleDataRow(ws, row, ["By Transaction ID", matchByTxn, pctOf(matchByTxn), ""], true);
        row += 1;
        styleDataRow(ws, row, ["By Email", matchByEmail, pctOf(matchByEmail), ""], false);
        row += 1;
        styleDataRow(ws, row, ["Other / Linked", matchOther, pctOf(matchOther), ""], true);
        row += 1;
        const unmatched = data.sublyticsOnly.length - data.ttMatched;
        const unmPct = data.sublyticsOnly.length > 0 ? ((unmatched / data.sublyticsOnly.length) * 100).toFixed(1) + "%" : "0%";
        styleDataRow(ws, row, ["Unmatched", unmatched, unmPct, ""], false);
        row += 2;
      }
    }

    if (Object.keys(data.summary.byClient || {}).length > 1) {
      styleSectionBanner(ws, row, "  Brand Summary", 3, "FF0F766E");
      row += 1;
      styleTableHeader(ws, row, ["Brand", "Orders", "Revenue ($)"], "FF115E59");
      row += 1;
      const brandStart = row;
      const brandEntries = Object.entries(data.summary.byClient || {}).sort(([a], [b]) => a.localeCompare(b));
      brandEntries.forEach(([brand, d], i) => {
        styleDataRow(ws, row, [brand, d.count, d.revenue], i % 2 === 1, [2]);
        row += 1;
      });
      const brandEnd = row - 1;
      styleTotalRow(ws, row, ["Total", { formula: `SUM(B${brandStart}:B${brandEnd})` }, { formula: `SUM(C${brandStart}:C${brandEnd})` }], [2]);
      row += 2;
    }

    const salesHeaders = [
      "Source", "Brand", "Order ID", "Transaction ID", "Customer", "Email",
      "Amount ($)", "Payment Type", "Status", "Date",
      "Sublytics Match", "TigerTech Match", "Gateway Match",
      "AUTH.NET Txn ID", "NMI Txn ID", "Stripe Txn ID", "Elavon Txn ID",
      "Matched Via", "Reconciliation",
    ];
    styleSectionBanner(ws, row, "  Individual Sales Data", salesHeaders.length, DARK_NAVY);
    row += 1;
    styleTableHeader(ws, row, salesHeaders, MED_NAVY);
    row += 1;
    const individualRecords =
      gridOrderedRecords && label === "All Clients"
        ? gridOrderedRecords
        : gridOrderedRecords && label !== "All Clients"
          ? gridOrderedRecords.filter((r) => (r.client || "") === label)
          : [...records].sort((a, b) => {
              const da = a.raw_date ? new Date(a.raw_date) : 0;
              const db = b.raw_date ? new Date(b.raw_date) : 0;
              return db - da;
            });
    individualRecords.forEach((r, i) => {
      const src = r.source ?? "";
      const isOrder = ["Sublytics", "Wordpress"].includes(src);
      const isGateway = ["AUTH.NET #1", "AUTH.NET #2", "NMI Payarc", "Stripe", "Elavon"].includes(src);
      const isTT = src === "TigerTech";
      const isRPM = src === "RPM";
      const isNexRep = src === "NexRep";
      const isNonMatch = ["Check", "Cash", "Reliable Solutions"].includes(src);

      let subMatch = "";
      if (isOrder) subMatch = "Source";
      else if (isNonMatch) subMatch = "N/A";
      else if (r._matched_sub_order_id) subMatch = `#${r._matched_sub_order_id}`;
      else subMatch = "Unmatched";

      // RPM / NexRep reconcile against Sublytics only — never TigerTech/gateways.
      let ttMatch = "";
      if (isTT) ttMatch = "Source";
      else if (isNonMatch || isRPM || isNexRep) ttMatch = "N/A";
      else ttMatch = r.matched_tigertech ? "Matched" : "No Match";

      let gwMatch = "";
      if (isGateway) gwMatch = "Source";
      else if (isNonMatch || isRPM || isNexRep) gwMatch = "N/A";
      else gwMatch = r.matched_gateway ? "Matched" : "Unmatched";

      let matchedVia = "";
      if (isOrder) {
        const raw = r.rawData || {};
        const parts = [];
        if ((raw.real_auth_transactions?.length || 0) > 0) parts.push("AUTH.NET");
        if ((raw.real_nmi_transactions?.length || 0) > 0 || raw._dashboard_linked_nmi) parts.push("NMI");
        if ((raw.real_stripe_transactions?.length || 0) > 0) parts.push("Stripe");
        if ((raw.real_reliable_transactions?.length || 0) > 0) parts.push("Reliable");
        if (parts.length === 0 && r.matched_gateway) {
          const gwSrc = r._matched_gateway_source;
          if (gwSrc?.startsWith("AUTH.NET")) parts.push("AUTH.NET");
          else if (gwSrc === "NMI Payarc") parts.push("NMI");
          else if (gwSrc === "Stripe") parts.push("Stripe");
          else if (gwSrc === "Reliable Solutions") parts.push("Reliable");
          else if (gwSrc === "Elavon") parts.push("Elavon");
        }
        if (raw.respective_tiger_tech_order) parts.push("TigerTech");
        matchedVia = parts.join(", ") || "—";
      } else if (r._matched_sub_order_id) {
        matchedVia = `Sub #${r._matched_sub_order_id}`;
      } else if (isNonMatch) {
        matchedVia = "N/A";
      } else {
        matchedVia = "—";
      }

      const reconMap = {
        matched: "Matched", matched_with_variance: "Variance",
        missing_in_gateway: "Missing", duplicate_candidate: "Duplicate",
        partially_matched: "Partial", ambiguous_match: "Ambiguous",
        settled_offline: "Offline", pending: "Pending",
      };
      const recon = isOrder ? (reconMap[r.reconciliation_status] || r.reconciliation_status || "—") : "";

      let authTxnId = "", nmiTxnId = "", stripeTxnId = "", elavonTxnId = "";
      if (isOrder) {
        const raw = r.rawData || {};
        authTxnId = (raw.real_auth_transactions || []).map((t) => t.transId || "").filter(Boolean).join(", ");
        nmiTxnId = (raw.real_nmi_transactions || []).map((t) => t.transaction_id || t.id || "").filter(Boolean).join(", ");
        if (!nmiTxnId && raw._dashboard_nmi_hints?.length) {
          nmiTxnId = raw._dashboard_nmi_hints.map((t) => t.transaction_id || t.id || "").filter(Boolean).join(", ");
        }
        stripeTxnId = (raw.real_stripe_transactions || []).map((t) => t.charge_id || t.id || "").filter(Boolean).join(", ");
        if (!authTxnId && !nmiTxnId && !stripeTxnId && r._matched_gateway_txn_id) {
          const gwSrc = r._matched_gateway_source || "";
          if (gwSrc.startsWith("AUTH.NET")) authTxnId = r._matched_gateway_txn_id;
          else if (gwSrc === "NMI Payarc") nmiTxnId = r._matched_gateway_txn_id;
          else if (gwSrc === "Stripe") stripeTxnId = r._matched_gateway_txn_id;
          else if (gwSrc === "Elavon") elavonTxnId = r._matched_gateway_txn_id;
        }
      } else if (src.startsWith("AUTH.NET")) {
        authTxnId = r.transaction_id ?? "";
      } else if (src === "NMI Payarc") {
        nmiTxnId = r.transaction_id ?? "";
      } else if (src === "Stripe") {
        stripeTxnId = r.transaction_id ?? "";
      } else if (src === "Elavon") {
        elavonTxnId = r.transaction_id ?? "";
      }

      const vals = [
        src,
        r.client ?? "",
        r.order_id ?? "",
        r.transaction_id ?? "",
        r.customer_name ?? "",
        r.customer_email ?? "",
        typeof r.amount === "number" ? r.amount : parseFloat(r.amount) || 0,
        describePaymentChannel(r),
        r.status ?? "",
        r.date ?? "",
        subMatch,
        ttMatch,
        gwMatch,
        authTxnId,
        nmiTxnId,
        stripeTxnId,
        elavonTxnId,
        matchedVia,
        recon,
      ];
      styleDataRow(ws, row, vals, i % 2 === 1, [6]);
      row += 1;
    });

    row += 1;
    const ttSubRows = records.filter((r) => r.source === "Sublytics" || r.source === "Wordpress");
    const ttSrcRows = records.filter((r) => r.source === "TigerTech");
    if (ttSubRows.length > 0 || ttSrcRows.length > 0) {
      const ttHeaders = [
        "Sub Order ID", "Sub Txn ID", "Sub Customer", "Sub Email", "Sub Amount", "Sub Status", "Sub Date",
        "Match Type",
        "TT Order #", "TT Txn ID", "TT Customer", "TT Email", "TT Amount", "TT Date",
      ];
      styleSectionBanner(ws, row, "  Sublytics ↔ TigerTech Matching", ttHeaders.length, "FF0891B2");
      row += 1;
      styleTableHeader(ws, row, ttHeaders, "FF0E7490");
      row += 1;

      const ttByEmail = new Map();
      const ttByTxn = new Map();
      const ttByOrder = new Map();
      for (const t of ttSrcRows) {
        const em = (t.customer_email || "").trim().toLowerCase();
        const mid = (t.transaction_id || "").trim().toLowerCase();
        const oid = (t.order_id || "").trim().toLowerCase();
        if (em && !em.includes("noemail")) { if (!ttByEmail.has(em)) ttByEmail.set(em, t); }
        if (mid) { if (!ttByTxn.has(mid)) ttByTxn.set(mid, t); }
        if (oid) { if (!ttByOrder.has(oid)) ttByOrder.set(oid, t); }
      }

      ttSubRows.forEach((s, i) => {
        const sEmail = (s.customer_email || "").trim().toLowerCase();
        const sTxn = (s.transaction_id || "").trim().toLowerCase();
        const sOid = (s.order_id || "").trim().toLowerCase();
        let tt = null;
        let matchType = "Unmatched";

        if (s.matched_tigertech && s.rawData?.respective_tiger_tech_order) {
          const tto = s.rawData.respective_tiger_tech_order;
          tt = {
            order_id: tto.order_number || "", transaction_id: tto.merchant_transaction_id || "",
            customer_email: tto.email || "",
            customer_name: `${tto.bill_first_name || ""} ${tto.bill_last_name || ""}`.trim(),
            amount: [tto.price01, tto.price02, tto.price03, tto.price04, tto.price05].reduce((a, p) => a + (parseFloat(p) || 0), 0),
            date: tto.order_date || "",
          };
          if (sTxn && ttByTxn.has(sTxn)) matchType = "Transaction ID";
          else if (sOid && ttByOrder.has(sOid)) matchType = "Order ID";
          else if (sEmail && ttByEmail.has(sEmail)) matchType = "Email";
          else matchType = "Linked";
        } else {
          if (sOid && ttByOrder.has(sOid)) { tt = ttByOrder.get(sOid); matchType = "Order ID"; }
          else if (sTxn && ttByTxn.has(sTxn)) { tt = ttByTxn.get(sTxn); matchType = "Transaction ID"; }
          else if (sEmail && !sEmail.includes("noemail") && ttByEmail.has(sEmail)) { tt = ttByEmail.get(sEmail); matchType = "Email"; }
        }

        const ttVals = [
          s.order_id ?? "", s.transaction_id ?? "", s.customer_name ?? "", s.customer_email ?? "",
          typeof s.amount === "number" ? s.amount : parseFloat(s.amount) || 0,
          s.status ?? "", s.date ?? "",
          matchType,
          tt?.order_id ?? "", tt?.transaction_id ?? "", tt?.customer_name ?? "", tt?.customer_email ?? "",
          tt ? (typeof tt.amount === "number" ? tt.amount : parseFloat(tt.amount) || 0) : "",
          tt?.date ?? "",
        ];
        styleDataRow(ws, row, ttVals, i % 2 === 1, [4, 12]);
        const mtCell = ws.getCell(row, 8);
        if (matchType === "Unmatched") {
          mtCell.font = { bold: true, size: 10, color: { argb: "FFDC2626" } };
        } else {
          mtCell.font = { bold: true, size: 10, color: { argb: "FF16A34A" } };
        }
        row += 1;
      });

      row += 1;
      const ttMatchedCount = ttSubRows.filter((s) => s.matched_tigertech).length;
      const ttTotalCount = ttSubRows.length;
      const ttPctVal = ttTotalCount > 0 ? ((ttMatchedCount / ttTotalCount) * 100).toFixed(1) : "0";
      ws.getCell(row, 1).value = "TT Match Summary";
      ws.getCell(row, 1).font = { bold: true, size: 11 };
      ws.getCell(row, 2).value = `${ttMatchedCount} / ${ttTotalCount} matched (${ttPctVal}%)`;
      ws.getCell(row, 2).font = { size: 11 };
      ws.getCell(row, 5).value = `TigerTech orders: ${ttSrcRows.length}`;
      ws.getCell(row, 5).font = { size: 11 };
    }
  }

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Sales-Dashboard-${dayjs().format("YYYY-MM-DD")}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Stock export: ShipStation-shaped sheet (row 1 = headers, Client in column A) so it aligns with
 * external "MERGED STOCK UPDATE" xlsx from ShipStation. Metadata lives on a second sheet so
 * VLOOKUP / diff tools on sheet 1 are not offset by blank column A or extra header rows.
 */
export const exportStockToExcel = async (stockData, columns, fileName, gridApi) => {
  if (!stockData || stockData.length === 0) return;

  const visibleCols = resolveVisibleColumns(gridApi, columns);
  const allDataCols = visibleCols;
  const brandOnlyCols = visibleCols.filter((c) => c.field !== "brand_name");
  const STOCK_LIMIT_LOW = 1000;
  const headerFont = { bold: true, size: 11 };
  const sectionFont = { bold: true, size: 11 };
  const reportDate = dayjs().format("MMM DD, YYYY HH:mm:ss");

  const brandGroups = {};
  stockData.forEach((item) => {
    const brand = item.brand_name || "Unknown";
    if (!brandGroups[brand]) brandGroups[brand] = [];
    brandGroups[brand].push(item);
  });
  const brandNames = Object.keys(brandGroups).sort();

  const wb = new ExcelJS.Workbook();
  wb.creator = "DataVision";

  const writeStockSheet = (ws, items, label, dataCols) => {
    const dataHeaders = dataCols.map((c) => c.headerName);
    const availIdx = dataCols.findIndex((c) => c.field === "available");
    let row = 1;

    ws.getCell(row, 1).value = "Stock Update Report";
    ws.getCell(row, 1).font = { bold: true, size: 12 };
    row += 1;
    ws.getCell(row, 1).value = "Client / Brand";
    ws.getCell(row, 2).value = label;
    ws.getCell(row, 1).font = headerFont;
    row += 1;
    ws.getCell(row, 1).value = "Report Generated On";
    ws.getCell(row, 2).value = reportDate;
    ws.getCell(row, 1).font = headerFont;
    row += 2;

    const totalSKUs = items.length;
    let totalOnHand = 0;
    let totalAvailable = 0;
    let totalBackorders = 0;
    let outOfStock = 0;
    let lowStock = 0;
    items.forEach((item) => {
      totalOnHand += item.on_hand || 0;
      totalAvailable += item.available || 0;
      totalBackorders += item.backorder_count || 0;
      if ((item.available || 0) <= 0) outOfStock++;
      else if ((item.available || 0) < STOCK_LIMIT_LOW) lowStock++;
    });

    ws.getCell(row, 1).value = "SUMMARY";
    ws.getCell(row, 1).font = sectionFont;
    row += 1;
    [
      ["Total SKUs", totalSKUs],
      ["Total On Hand", totalOnHand],
      ["Total Available", totalAvailable],
      ["Total Back Orders", totalBackorders],
      ["Low Stock (< 1000)", lowStock],
      ["Out of Stock", outOfStock],
    ].forEach(([lbl, val]) => {
      ws.getCell(row, 1).value = lbl;
      ws.getCell(row, 1).font = headerFont;
      ws.getCell(row, 2).value = val;
      row += 1;
    });
    row += 1;

    ws.getCell(row, 1).value = "INVENTORY DATA";
    ws.getCell(row, 1).font = sectionFont;
    row += 1;
    const headerRow = ws.getRow(row);
    dataHeaders.forEach((h, i) => {
      const cell = headerRow.getCell(i + 1);
      cell.value = h;
      cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1A365D" } };
      cell.alignment = { vertical: "middle", horizontal: "left" };
      cell.border = thinBorder;
    });
    headerRow.height = 22;
    row += 1;

    items.forEach((item, ri) => {
      const dataRow = ws.getRow(row);
      dataCols.forEach((c, ci) => {
        const cell = dataRow.getCell(ci + 1);
        const val = item[c.field];
        cell.value = typeof val === "number" ? val : val ?? "";
        cell.border = thinBorder;
        if (ri % 2 === 1) {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF7FAFC" } };
        }
      });
      if (availIdx >= 0) {
        const stockVal = item.available ?? 0;
        const cell = dataRow.getCell(availIdx + 1);
        if (stockVal <= 0) {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFCDD2" } };
          cell.font = { bold: true, color: { argb: "FFB71C1C" } };
        } else if (stockVal < STOCK_LIMIT_LOW) {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFF3E0" } };
          cell.font = { bold: true, color: { argb: "FFE65100" } };
        }
      }
      row += 1;
    });

    dataCols.forEach((c, i) => {
      let max = String(dataHeaders[i] || "").length;
      items.forEach((item) => {
        max = Math.max(max, String(item[c.field] ?? "").length);
      });
      ws.getColumn(i + 1).width = Math.min(Math.max(max * 0.95 + 2.5, 12), 45);
    });
  };

  const allSheet = wb.addWorksheet("All Clients", { views: [{ state: "frozen", ySplit: 0 }] });
  writeStockSheet(allSheet, stockData, "All Clients", allDataCols);

  brandNames.forEach((brand) => {
    const safeName = brand.replace(/[[\]*?/\\:]/g, "_").slice(0, 31);
    const ws = wb.addWorksheet(safeName, { views: [{ state: "frozen", ySplit: 0 }] });
    writeStockSheet(ws, brandGroups[brand], brand, brandOnlyCols);
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Stock-Update-${dayjs().format("YYYY-MM-DD")}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
};
